#!/usr/bin/env node
/**
 * HoloScript MCP Server - Streamable HTTP Transport
 *
 * Production HTTP transport for HoloScript language tooling.
 * Enables remote AI agents to parse, validate, and generate HoloScript code.
 *
 * Security Architecture (AAIF Enterprise):
 * - OAuth 2.1 authentication (PKCE mandatory, token rotation, DPoP support)
 * - Triple-gate security pattern:
 *   Gate 1: Client->LLM prompt validation (size, depth, injection, rate limit)
 *   Gate 2: LLM->MCP tool authorization (per-tool OAuth scopes)
 *   Gate 3: MCP->downstream API (StdlibPolicy enforcement)
 * - EU AI Act compliant audit logging (Articles 12-14)
 * - Backwards-compatible with legacy API key during migration period
 */

// !! MUST be first import — populates process.env from .env before any module-level
// `const X = process.env.X || ''` constants are evaluated. No-op on Railway.
import './utils/load-env';

import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { SSEServerTransport } from '@modelcontextprotocol/sdk/server/sse.js';
import { CallToolRequestSchema, ListToolsRequestSchema } from '@modelcontextprotocol/sdk/types.js';
import {
  ListResourcesRequestSchema,
  ReadResourceRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';
import { randomUUID, createHash } from 'crypto';
import http from 'http';
import { resolveServiceSecret, migrateEnvKeys } from './holokey-resolver';
import { createRailwayPostgresPoolOptions } from './postgres-pool-options';
import { getMcpServerSizing } from './server-sizing';
import { handlePartnerRegistryValidateRoute } from './partner-registry-route';
import { tools } from './tools';
import { handleTool } from './handlers';
import { _handleSingleToolLogic } from './index';
import { listSkillResources, readSkillResource } from './skill-resources';
import { PluginManager } from './PluginManager';
import { handleCompilerTool, handleCompileToTarget } from './compiler-tools';
import { verifyCaelResult } from './verify-cael';
import {
  renderPreview,
  createShareLink,
  getScene,
  storeScene,
  listRecentPublicScenes,
  type SceneProvenance,
  findSceneByAuthor,
  generateBrowserTemplate,
  generateWebGPUBrowserTemplate,
  generateThumbnail,
  getThumbnail,
} from './renderer';

import {
  buildAgentCard,
  createTask,
  getTask,
  listTasks,
  cancelTask,
  executeTask,
  taskToResponse,
  handleJsonRpcRequest,
  parseJsonRpcRequest,
  type SendTaskRequest,
  type TaskMessage,
  type TaskState,
  type AgentCard,
  deriveSkillTags,
} from './a2a';
import {
  getOAuth21Service,
  SCOPE_CATEGORIES,
  type TokenIntrospection,
  type OAuthScope,
} from './security/oauth21';
import type { TokenIntrospectionWithTenant } from './security/tenant-auth';
import { runTripleGate } from './security/gates';
import { getAuditLogger, type AuditEventType, type AuditResultStatus } from './security/audit-log';
import {
  checkRateLimitBypass,
  readBearerToken,
  readXForwardedFor,
} from './security/bypass-detection';
import {
  checkConsumerGlobalSpendCap,
  recordConsumerGeneration,
} from './security/consumer-spend-guard';
import { ensureMcpOtelTracer, withMcpToolExecutionSpan } from './telemetry/mcp-tool-tracing';
import { getOAuth2Provider, OAUTH2_SCOPES } from './auth/oauth2-provider';
import type { TokenStoreBackend } from './auth/token-store';
import { PostgresTokenStore } from './auth/postgres-token-store';
import {
  handleInboundGossip,
  HoloMeshWorldState,
  HoloMeshDiscovery,
  registerSearchProviders,
  setTeamFormationRosterSource,
} from './holomesh/index';
import { getClient as getHoloMeshOrchestratorClient } from './holomesh/orchestrator-client';
import { applyEdgeSafeSseHeaders } from './holomesh/sse-edge-headers';
import {
  extractAndVerifySigning,
  type SigningContext,
} from './holomesh/identity/signing-middleware';
import { frameDeclarationFromMcpMeta, gateToolCall } from './tool-call-gate';
import { founderGateX402ToolCallCheck } from './tool-call-checks';
import { initDurableAttestationRegistry } from './holomesh/identity/attestation-persistence';
import {
  initStores,
  teamStore,
  playerStore,
  inviteStore,
  geoAnchorStore,
  stateStore,
  keyRegistry,
  agentKeyStore,
  teamPresenceStore,
  reloadTeam,
} from './holomesh/state';
import { hydrateEmergenceFromCorpus } from './daemon-lifecycle-tools';
import { startCiPublicWorker } from './ci-public-worker';
import { getConsolidationBridge } from './holomesh/consolidation-bridge';
import { queryAdminOperationsAudit } from './holomesh/admin-operations-audit';
import { loadNativeAgentCompositions } from './holomesh/agent/loader';
import type { GossipDeltaRequest } from './holomesh/types';
import { recordMcpToolCallMetric } from './ops/tool-anomaly-bridge.js';
import {
  maybeStartRailwayAutoscaleLoop,
  registerMcpAutoscaleSessions,
} from './ops/railway-autoscale-loop.js';
import { maybeStartPredictiveCloudflareLbLoop } from './ops/predictive-cloudflare-lb.js';
import { maybeStartKeepAliveLoop, getKeepAliveStatus } from './ops/keep-alive.js';

// Initialize native agent compositions
loadNativeAgentCompositions();
import { WebRTCSignalingServer } from './holomesh/webrtc-signaling';
import { formatBroadcastContextMarkdown } from './holomesh/moltbook-broadcast-context';
import { buildMoltbookCrosspostPayload, createMoltbookPost } from './moltbook/moltbook-post.js';
import { resolveSecretWithLease, VaultLeaseError } from './holomesh/identity/vault-lease-registry';

/**
 * Phase 3 wrapper around `process.env.MOLTBOOK_API_KEY` for the per-request
 * `POST /api/moltbook/crosspost` route. Each invocation is a per-task MCP
 * fetch helper reading an outbound API key — Phase 3 medium-risk tier.
 *
 * Returning `''` on `VaultLeaseError` preserves the original
 * `process.env.MOLTBOOK_API_KEY || ''` semantics. The handler branches on
 * the empty-string falsy case to emit a 503.
 */
function readMoltbookApiKeyForRoute(): string {
  try {
    return resolveSecretWithLease('env:MOLTBOOK_API_KEY') ?? '';
  } catch (err) {
    if (err instanceof VaultLeaseError) return '';
    throw err;
  }
}

import { resolveStoreRoot } from './hologram-renderer';
import { isHologramMcpResponse, wrapHologramMcpEnvelope, parseHolo } from '@holoscript/core';
import { buildContentPolicyConfig, evaluateContentPolicySync } from '@holoscript/core/policy';
import { promises as fsPromises } from 'fs';
import { join as pathJoin, extname as pathExtname, basename as pathBasename } from 'path';
import { handleSovereign3DRoute } from './sovereign-3d-backend';

const { OPERATION_COSTS } = require('@holoscript/absorb-service/credits') as {
  OPERATION_COSTS: Record<string, { baseCostCents: number; description: string }>;
};

/** Revenue distribution result from @holoscript/core (not in dist/index.d.ts, so defined locally) */
interface RevenueDistributionResult {
  totalPrice: bigint;
  flows: Array<{ recipient: string; amount: bigint; reason: string; bps: number; depth?: number }>;
}

const PORT = parseInt(process.env.PORT || '3000', 10);
const HOLOSCRIPT_API_KEY = process.env.HOLOSCRIPT_API_KEY || '';
const SERVICE_NAME = 'holoscript-mcp';
declare const __SERVICE_VERSION__: string;
const SERVICE_VERSION = typeof __SERVICE_VERSION__ !== 'undefined' ? __SERVICE_VERSION__ : '0.0.0';
const SERVER_SIZING = getMcpServerSizing();

const IS_RAILWAY = Boolean(
  process.env.RAILWAY_PUBLIC_DOMAIN ||
  process.env.RAILWAY_PROJECT_ID ||
  process.env.RAILWAY_ENVIRONMENT
);
/** SSE MCP session mode: off on Railway unless opted in (multi-replica routing can split GET/POST). */
const ALLOW_SSE_TRANSPORT = process.env.MCP_ENABLE_SSE === 'true' || !IS_RAILWAY;

// Protocol Registry — in-memory store (production: back with PostgreSQL)
const protocolRecords = new Map<string, Record<string, unknown>>();
const compileRateMap = new Map<string, number[]>();
const protocolMetadata = new Map<string, Record<string, unknown>>();

// Initialize PostgreSQL pool (shared across token store + credit routes)
let pgPool: import('pg').Pool | undefined;
let tokenBackend: TokenStoreBackend | undefined;
if (process.env.DATABASE_URL) {
  try {
    const { Pool } = require('pg') as typeof import('pg');
    pgPool = new Pool(
      createRailwayPostgresPoolOptions(process.env.DATABASE_URL, {
        max: SERVER_SIZING.postgresPoolMax,
      })
    );
    tokenBackend = new PostgresTokenStore(pgPool);
    // Auto-create credit tables if they don't exist
    pgPool
      .query(
        `
      CREATE TABLE IF NOT EXISTS credit_accounts (
        user_id UUID PRIMARY KEY,
        balance_cents INTEGER NOT NULL DEFAULT 0,
        lifetime_spent_cents INTEGER NOT NULL DEFAULT 0,
        lifetime_purchased_cents INTEGER NOT NULL DEFAULT 0,
        tier VARCHAR(16) NOT NULL DEFAULT 'free',
        free_credits_used_cents INTEGER NOT NULL DEFAULT 0,
        created_at TIMESTAMP NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMP NOT NULL DEFAULT NOW()
      );
      CREATE TABLE IF NOT EXISTS credit_transactions (
        id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
        user_id UUID NOT NULL,
        type VARCHAR(16) NOT NULL,
        amount_cents INTEGER NOT NULL,
        balance_after_cents INTEGER NOT NULL,
        description TEXT NOT NULL,
        metadata JSONB DEFAULT '{}',
        stripe_session_id TEXT,
        created_at TIMESTAMP NOT NULL DEFAULT NOW()
      );
      CREATE INDEX IF NOT EXISTS idx_credit_tx_user ON credit_transactions (user_id);
      CREATE INDEX IF NOT EXISTS idx_credit_tx_type ON credit_transactions (type);
      CREATE INDEX IF NOT EXISTS idx_credit_tx_time ON credit_transactions (created_at);
    `
      )
      .then(() => console.info('[credits] Tables ready'))
      .catch((e: Error) =>
        console.error(
          `[credits] Migration failed: ${e.message}. Credit tracking will not persist. Check DATABASE_URL and ensure PostgreSQL is reachable.`
        )
      );
  } catch (err) {
    console.error(
      `[auth] Failed to initialize PostgreSQL pool: ${(err as Error)?.message ?? err}. Verify DATABASE_URL is set and the database is running. Falling back to in-memory token store — tokens will not survive restarts.`
    );
    console.warn('[auth] Using in-memory token store (DATABASE_URL connection failed)');
  }
} else {
  console.warn('[auth] Using in-memory token store (no DATABASE_URL)');
}

// Initialize security services
const oauth = getOAuth21Service({
  legacyApiKey: HOLOSCRIPT_API_KEY,
  migrationMode: (process.env.OAUTH_MIGRATION_MODE as 'strict' | 'permissive') || 'permissive',
});
const oauth2 = getOAuth2Provider({
  legacyApiKey: HOLOSCRIPT_API_KEY,
  migrationMode: (process.env.OAUTH_MIGRATION_MODE as 'strict' | 'permissive') || 'permissive',
  backend: tokenBackend,
});
const auditLog = getAuditLogger();

// ── Durable OAuth bridge (task_1781078719152_ym9w) ──────────────────────────
// The legacy oauth21 registry is module-level in-memory and wiped on EVERY
// deploy. With push→deploy firing many times a day, every cached client_id and
// live Bearer died on each push — surfacing as raw invalid_client JSON tabs in
// the founder's browser whenever a Claude instance (re)connected via
// mcp-remote, whose OAuth fallback opens a browser and has no headless mode.
// Bridge strategy: register/issue through the legacy service (sync, unchanged
// call sites), write clients + tokens through to the Postgres-backed
// OAuth2Provider, and rehydrate the legacy maps from it on miss.

oauth.setDurableIntrospector(async (token) => {
  const result = await oauth2.introspect(token);
  if (!result.active) return undefined;
  return {
    active: true,
    clientId: result.clientId,
    scopes: (result.scopes || []) as OAuthScope[],
    agentId: result.agentId,
    expiresAt: result.expiresAt,
    issuedAt: result.issuedAt,
  };
});

/** Rehydrate a client from the durable registry into the legacy in-memory one. */
async function ensureClientHydrated(clientId: string | null | undefined): Promise<void> {
  if (!clientId || oauth.getClient(clientId)) return;
  try {
    const durable = await oauth2.getClient(clientId);
    if (durable) {
      oauth.importClient({
        clientId: durable.clientId,
        clientSecret: durable.clientSecretHash,
        clientName: durable.clientName,
        redirectUris: durable.redirectUris,
        scopes: durable.scopes as OAuthScope[],
        createdAt: durable.createdAt,
        clientType: durable.clientType,
        rateLimit: durable.rateLimit,
      });
    }
  } catch (err) {
    console.warn(
      '[auth] durable client hydration failed:',
      err instanceof Error ? err.message : err
    );
  }
}

/** Rehydrate a refresh token from the durable registry before a refresh grant. */
async function ensureRefreshTokenHydrated(refreshToken: string | undefined): Promise<void> {
  if (!refreshToken || oauth.getRefreshTokenRecord(refreshToken)) return;
  try {
    const durable = await oauth2.getStoredRefreshToken(refreshToken);
    if (durable && !durable.used && durable.expiresAt > Date.now()) {
      oauth.importRefreshToken({
        token: durable.token,
        clientId: durable.clientId,
        scopes: durable.scopes as OAuthScope[],
        issuedAt: durable.issuedAt,
        expiresAt: durable.expiresAt,
        chainId: durable.chainId,
        used: durable.used,
      });
    }
  } catch (err) {
    console.warn(
      '[auth] durable refresh-token hydration failed:',
      err instanceof Error ? err.message : err
    );
  }
}

/** Write tokens issued by the legacy service through to the durable registry. */
async function persistIssuedTokens(
  tokenResponse: {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    scope?: string;
  },
  clientId: string,
  agentId?: string
): Promise<void> {
  try {
    const now = Date.now();
    const scopes = (tokenResponse.scope || '').split(' ').filter(Boolean);
    if (tokenResponse.access_token) {
      await oauth2.importAccessToken({
        token: tokenResponse.access_token,
        clientId,
        scopes,
        issuedAt: now,
        expiresAt: now + (tokenResponse.expires_in || 3600) * 1000,
        agentId,
      });
    }
    if (tokenResponse.refresh_token) {
      const record = oauth.getRefreshTokenRecord(tokenResponse.refresh_token);
      await oauth2.importRefreshToken({
        token: tokenResponse.refresh_token,
        clientId,
        scopes: record ? (record.scopes as string[]) : scopes,
        issuedAt: record?.issuedAt ?? now,
        expiresAt: record?.expiresAt ?? now + 86_400_000,
        chainId: record?.chainId ?? tokenResponse.refresh_token,
        used: false,
      });
    }
  } catch (err) {
    console.warn(
      '[auth] durable token write-through failed:',
      err instanceof Error ? err.message : err
    );
  }
}

// ── Simple per-IP rate limiter for OAuth endpoints ──────────────────────────
const RATE_LIMIT = SERVER_SIZING.oauthRateLimit;
const RATE_WINDOW_MS = 60_000;
const rateBuckets = new Map<string, { count: number; resetAt: number }>();

// ── Anonymous public tier (no API key required) ─────────────────────────────
// Read-only / inert tools agents and humans can call without registering.
// Designed for first-touch discoverability: parse a `.holo` file, look up
// docs/examples, validate syntax, list compile targets. Anything that
// reaches an LLM, writes state, or costs real money stays behind /oauth.
const PUBLIC_ANON_TOOLS: ReadonlySet<string> = new Set([
  'parse_holo',
  'validate_holoscript',
  'explain_trait',
  'get_syntax_reference',
  'get_examples',
  'list_export_targets',
]);
const PUBLIC_ANON_RATE_LIMIT = SERVER_SIZING.publicAnonRateLimit;

// ── WS-1 (2026-07-02): anonymous consumer generation tier ──────────────────
// Deliberately SEPARATE from PUBLIC_ANON_TOOLS/_handleSingleToolLogic above --
// that path bypasses runForkSandboxGate entirely (verified: index.ts
// _handleSingleToolLogic dispatches straight off TOOL_DISPATCH_REGISTRY, no
// gate call). generate_scene/generate_world_from_prompt reach an LLM and must
// stay gated. This endpoint routes through handleTool() -> runForkSandboxGate()
// with source:'consumer', which resolves only these two tool names to the narrow
// DEFAULT_CONSUMER_GENERATION_POLICY (see sandbox-policy.ts resolvePolicy()).
const CONSUMER_GENERATION_TOOLS: ReadonlySet<string> = new Set([
  'generate_scene',
  'generate_world_from_prompt',
]);
const CONSUMER_GEN_RATE_LIMIT = SERVER_SIZING.consumerGenRateLimit;
// Mandatory daily quota per IP (founder decision 2026-07-02: a per-minute rate
// limit alone bounds RATE, not total distinct-IP fan-out COST against an LLM
// that anonymous, unauthenticated traffic can reach). Local counter -- kept
// inside mcp-server's own dependency graph rather than importing studio's
// creditGate cross-package for a security-sensitive anonymous path.
const CONSUMER_GEN_DAILY_QUOTA = SERVER_SIZING.consumerGenDailyQuota;
const consumerGenDailyBuckets = new Map<string, { count: number; resetAt: number }>();
function checkConsumerGenDailyQuota(ip: string): {
  allowed: boolean;
  remaining: number;
  resetAt: number;
} {
  const now = Date.now();
  let bucket = consumerGenDailyBuckets.get(ip);
  if (!bucket || bucket.resetAt <= now) {
    bucket = { count: 0, resetAt: now + 24 * 60 * 60 * 1000 };
    consumerGenDailyBuckets.set(ip, bucket);
  }
  const allowed = bucket.count < CONSUMER_GEN_DAILY_QUOTA;
  if (allowed) bucket.count++;
  return {
    allowed,
    remaining: Math.max(0, CONSUMER_GEN_DAILY_QUOTA - bucket.count),
    resetAt: bucket.resetAt,
  };
}
// Family-tier content policy (same tier/pattern as hololandFamilyPolicyConfig in
// hololand-mcp-tools.ts) -- anonymous callers get the strictest borderline
// threshold and mandatory LLM consult, not a looser default.
const consumerGenerationPolicyConfig = buildContentPolicyConfig({
  tier: 'family',
  region: process.env.HOLOLAND_POLICY_REGION || 'GLOBAL',
});

function getRateLimit(
  ip: string,
  dynamicLimit?: number
): { remaining: number; limit: number; resetAt: number } {
  const now = Date.now();
  const activeLimit = dynamicLimit ?? RATE_LIMIT;
  let bucket = rateBuckets.get(ip);
  if (!bucket || bucket.resetAt <= now) {
    bucket = { count: 0, resetAt: now + RATE_WINDOW_MS };
    rateBuckets.set(ip, bucket);
  }
  bucket.count++;
  return {
    remaining: Math.max(0, activeLimit - bucket.count),
    limit: activeLimit,
    resetAt: bucket.resetAt,
  };
}

function setRateLimitHeaders(
  res: http.ServerResponse,
  rl: { remaining: number; limit: number; resetAt: number }
) {
  res.setHeader('X-RateLimit-Limit', rl.limit);
  res.setHeader('X-RateLimit-Remaining', rl.remaining);
  res.setHeader('X-RateLimit-Reset', Math.ceil(rl.resetAt / 1000));
}

// ── Tool category labels for discovery ───────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
  parsing: 'Parsing & Validation',
  traits: 'Traits',
  generation: 'Code Generation',
  codebase: 'Codebase Intelligence',
  search: 'Semantic Search',
  graph: 'Graph Analysis',
  quality: 'Quality & Testing',
  filesystem: 'File Operations',
  knowledge: 'Knowledge & Patterns',
  refactoring: 'Refactoring',
  ide: 'IDE / LSP',
  ai: 'AI Assistant',
  browser: 'Browser Control',
  compilation: 'Compilation & Export',
  networking: 'Networking',
  temporal: 'Temporal Snapshots',
  monitoring: 'Monitoring',
  rendering: 'Rendering & Sharing',
  gltf: 'glTF Import/Export',
  agent: 'Agent Orchestration',
  absorb: 'Absorb Service',
  testing: 'Testing',
  documentation: 'Documentation',
  training: 'Training Data',
  '3d': '3D Generation',
  conversion: 'Format Conversion',
  contract: 'Service Contracts',
  utility: 'Utility',
};

// Store active transports by session ID
const transports = new Map<string, SSEServerTransport>();
registerMcpAutoscaleSessions(() => transports.size);

// ── Session-to-Auth mapping for MCP transport sessions ───────────────────────
const sessionAuth = new Map<string, TokenIntrospection>();

async function createAndStoreSessionTransport(
  req: http.IncomingMessage,
  res: http.ServerResponse,
  auth?: TokenIntrospection
): Promise<{ transport: SSEServerTransport; sid: string }> {
  const sid = randomUUID();
  const host = req.headers.host || `localhost:${PORT}`;
  const protocol = req.headers['x-forwarded-proto'] || 'http';
  const baseUrl = `${protocol}://${host}`;
  applyEdgeSafeSseHeaders(res);
  const transport = new SSEServerTransport(`${baseUrl}/mcp/messages?sessionId=${sid}`, res);

  const server = createMcpServer(auth);
  await server.connect(transport);

  transports.set(sid, transport);

  if (auth) {
    sessionAuth.set(sid, auth);
  }

  auditLog.logSessionEvent({
    event: 'session_created',
    sessionId: sid,
    clientId: auth?.clientId,
  });

  transport.onclose = () => {
    auditLog.logSessionEvent({
      event: 'session_closed',
      sessionId: sid,
      clientId: auth?.clientId,
    });
    sessionAuth.delete(sid);
    transports.delete(sid);
  };

  return { transport, sid };
}

// ── Authentication ───────────────────────────────────────────────────────────

/**
 * Authenticate an HTTP request via OAuth 2.1 or legacy API key.
 * Returns token introspection with scopes and agent identity.
 */
async function authenticateRequest(req: http.IncomingMessage): Promise<TokenIntrospection> {
  const headers = { ...req.headers };
  const queryString = req.url?.split('?')[1] || '';
  const params = new URLSearchParams(queryString);
  const apiKey = params.get('apiKey') || params.get('key');
  if (apiKey) {
    headers['x-mcp-api-key'] = apiKey;
  }
  return oauth.authenticateRequestAsync(headers as Record<string, string | string[] | undefined>);
}

/**
 * Legacy authentication check (kept for simple boolean checks on non-tool routes)
 */

async function checkAuth(req: http.IncomingMessage): Promise<boolean> {
  const auth = await authenticateRequest(req);
  return auth.active;
}

/**
 * Check if the request carries a founder-level key from the HoloMesh key
 * registry. Founders bypass credit gates — their operations are always
 * authorized regardless of credit_accounts balance.
 *
 * Uses the HoloMesh keyRegistry (same store as resolveRequestingAgent) so
 * founder status is consistent with the REST auth layer.
 */
function isFounderRequest(req: http.IncomingMessage): boolean {
  const auth = req.headers['authorization'];
  const mcpKey = req.headers['x-mcp-api-key'];
  let token: string | undefined;
  if (typeof auth === 'string' && auth.startsWith('Bearer ')) {
    token = auth.slice(7).trim();
  } else if (typeof mcpKey === 'string' && mcpKey.length > 0) {
    token = mcpKey.trim();
  }
  if (!token) return false;
  const record = keyRegistry.get(token);
  return record?.isFounder === true;
}

/**
 * Get client IP from request (for audit logging)
 */
function getClientIP(req: http.IncomingMessage): string {
  const forwarded = req.headers['x-forwarded-for'];
  if (typeof forwarded === 'string') return forwarded.split(',')[0].trim();
  return req.socket.remoteAddress || 'unknown';
}

/**
 * Parse request body from incoming request.
 * Supports both application/json and application/x-www-form-urlencoded (RFC 6749).
 */
function parseJsonBody(req: http.IncomingMessage): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    let totalSize = 0;
    const MAX_BODY = SERVER_SIZING.requestBodyMaxBytes;

    req.on('data', (chunk: Buffer) => {
      totalSize += chunk.length;
      if (totalSize > MAX_BODY) {
        reject(new Error('Request body too large'));
        req.destroy();
        return;
      }
      chunks.push(chunk);
    });
    req.on('end', () => {
      try {
        const body = Buffer.concat(chunks).toString('utf-8');
        if (!body) {
          resolve({});
          return;
        }
        const ct = (req.headers['content-type'] || '').toLowerCase();
        if (ct.includes('application/x-www-form-urlencoded')) {
          const params = new URLSearchParams(body);
          const obj: Record<string, unknown> = {};
          for (const [k, v] of params) obj[k] = v;
          resolve(obj);
        } else {
          resolve(JSON.parse(body));
        }
      } catch {
        reject(new Error('Invalid request body'));
      }
    });
    req.on('error', reject);
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function isSignedRequestEnvelope(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (value.envelope_type === 'dual' || value.envelope_type === 'capability') return true;
  return (
    isRecord(value.body) &&
    typeof value.signature === 'string' &&
    typeof value.signer_address === 'string' &&
    typeof value.nonce === 'string' &&
    typeof value.timestamp === 'string'
  );
}

function mergeSigningContextScopes(
  signingCtx: SigningContext,
  auth: TokenIntrospection
): SigningContext {
  const scopes = new Set<string>([
    ...(signingCtx.scopes ?? []).map(String),
    ...(auth.scopes ?? []).map(String),
  ]);
  return {
    ...signingCtx,
    scopes: [...scopes],
  };
}

async function unwrapSignedHttpBody(
  rawBody: Record<string, unknown>,
  auth: TokenIntrospection
): Promise<{ body: Record<string, unknown>; signingCtx?: SigningContext; signingError?: string }> {
  if (!isSignedRequestEnvelope(rawBody)) return { body: rawBody };
  const { effectiveBody, ctx } = await extractAndVerifySigning(rawBody, { strictMode: true });
  if (!ctx.signingValid) {
    return { body: {}, signingCtx: ctx, signingError: ctx.signingReason || 'signing-invalid' };
  }
  if (!isRecord(effectiveBody)) {
    return { body: {}, signingCtx: ctx, signingError: 'signed-body-not-object' };
  }
  return {
    body: effectiveBody,
    signingCtx: mergeSigningContextScopes(ctx, auth),
  };
}

// ── Secured Tool Execution ───────────────────────────────────────────────────

/**
 * Execute a tool call with full triple-gate security and audit logging.
 *
 * This is the central secured execution path. All tool calls from MCP sessions,
 * JSON-RPC fallback, and A2A tasks route through here.
 */
interface SecuredToolExecutionOptions {
  sessionId?: string;
  requestPath?: string;
  requestMethod?: string;
  ip?: string;
  tcpPeerIp?: string;
  rawXForwardedFor?: string;
  bearerToken?: string;
  signingCtx?: SigningContext;
}

async function securedToolExecution(
  toolName: string,
  args: Record<string, unknown>,
  auth: TokenIntrospection,
  options?: SecuredToolExecutionOptions
): Promise<{ result: unknown; isError: boolean }> {
  return withMcpToolExecutionSpan(toolName, auth, () =>
    securedToolExecutionInner(toolName, args, auth, options)
  );
}

async function securedToolExecutionInner(
  toolName: string,
  args: Record<string, unknown>,
  auth: TokenIntrospection,
  options?: SecuredToolExecutionOptions
): Promise<{ result: unknown; isError: boolean }> {
  const startTime = Date.now();
  let toolMetricError = false;

  try {
    const bypass = await checkRateLimitBypass({
      toolName,
      directIp: options?.ip,
      tcpPeerIp: options?.tcpPeerIp,
      rawXForwardedFor: options?.rawXForwardedFor,
      bearerToken: options?.bearerToken,
    });
    if (!bypass.allowed) {
      toolMetricError = true;
      return {
        result: {
          error: 'Request blocked by rate-limit bypass heuristics',
          code: bypass.reason || 'bypass_denied',
        },
        isError: true,
      };
    }

    // Run triple-gate security check
    const gateResult = runTripleGate(toolName, args, auth);

    // Audit log the invocation (pass or fail)
    const invocationId = auditLog.logToolInvocation({
      toolName,
      args,
      auth,
      gateResult,
      requestPath: options?.requestPath,
      requestMethod: options?.requestMethod,
      ip: options?.ip,
      sessionId: options?.sessionId,
    });

    // If gates failed, return denial
    if (!gateResult.passed) {
      toolMetricError = true;
      auditLog.logToolResult({
        invocationId,
        toolName,
        status: 'denied',
        durationMs: Date.now() - startTime,
        errorMessage: gateResult.reason,
        auth,
        sessionId: options?.sessionId,
      });

      return {
        result: {
          error: `Security gate ${gateResult.gate} denied: ${gateResult.reason}`,
          gate: gateResult.gate,
          riskLevel: gateResult.riskLevel,
        },
        isError: true,
      };
    }

    // Enforce Tenant Config / Billing Limits
    const tenantCtx = (auth as TokenIntrospectionWithTenant).tenantContext;
    if (tenantCtx) {
      const rl = getRateLimit(
        `tenant_${tenantCtx.tenantId}`,
        tenantCtx.limits.rateLimitRequestsPerMin
      );
      if (rl.remaining <= 0) {
        toolMetricError = true;
        return {
          result: {
            error: `Tenant rate limit exceeded (${tenantCtx.limits.rateLimitRequestsPerMin} req/min). Please upgrade tier from ${tenantCtx.subscriptionTier}.`,
          },
          isError: true,
        };
      }

      // Abstracted async usage tracking for billing
      const billingPool = pgPool;
      if (billingPool) {
        Promise.resolve().then(async () => {
          try {
            await billingPool.query(
              'UPDATE api_keys SET usage_count = usage_count + 1, spent_usd = spent_usd + 0.001 WHERE tenant_id = $1',
              [tenantCtx.tenantId]
            );
          } catch (e) {
            console.error('[Telemetry] Failed to update usage:', e);
          }
        });
      }
    }

    const signingCtx: SigningContext = options?.signingCtx ?? {
      signedRequest: false,
      signingValid: true,
      signer: auth.agentId ?? auth.clientId ?? null,
      scopes: auth.scopes ?? [],
    };

    // Execute the tool
    try {
      const dispatchResult = await _handleSingleToolLogic(toolName, args, signingCtx);

      if ((dispatchResult as { isError?: boolean }).isError) {
        const errorText =
          (dispatchResult as { content?: Array<{ text?: string }> }).content?.[0]?.text ||
          `Tool execution failed: ${toolName}`;
        throw new Error(errorText);
      }

      const resultText =
        (dispatchResult as { content?: Array<{ text?: string }> }).content?.[0]?.text ?? 'null';
      let result: unknown = resultText;
      try {
        result = JSON.parse(resultText);
      } catch {
        // keep raw text result
      }

      auditLog.logToolResult({
        invocationId,
        toolName,
        status: 'success',
        durationMs: Date.now() - startTime,
        auth,
        sessionId: options?.sessionId,
      });

      return { result, isError: false };
    } catch (error) {
      toolMetricError = true;
      const message = error instanceof Error ? error.message : String(error);
      console.error(`[MCP] Tool error: ${toolName}`, message);

      auditLog.logToolResult({
        invocationId,
        toolName,
        status: 'error',
        durationMs: Date.now() - startTime,
        errorMessage: message,
        auth,
        sessionId: options?.sessionId,
      });

      return { result: { error: message }, isError: true };
    }
  } finally {
    recordMcpToolCallMetric(toolName, Date.now() - startTime, toolMetricError);
  }
}

// ── MCP Server Factory ──────────────────────────────────────────────────────

/**
 * Create MCP server instance with security-aware tool handling.
 */
function createMcpServer(sessionAuthContext?: TokenIntrospection): Server {
  const server = new Server(
    {
      name: SERVICE_NAME,
      version: SERVICE_VERSION,
    },
    {
      capabilities: {
        tools: {},
        resources: {},
      },
    }
  );

  // List available skill resources
  server.setRequestHandler(ListResourcesRequestSchema, async () => {
    return { resources: listSkillResources() };
  });

  // Read a specific skill resource
  server.setRequestHandler(ReadResourceRequestSchema, async (request) => {
    const uri = request.params.uri;
    const result = readSkillResource(uri);
    if (!result) {
      throw new Error(`Resource not found: ${uri}`);
    }
    return {
      contents: [
        {
          uri,
          mimeType: result.mimeType,
          text: result.text,
        },
      ],
    };
  });

  // List available tools
  server.setRequestHandler(ListToolsRequestSchema, async () => {
    return {
      tools: [...tools, ...PluginManager.getTools()],
    };
  });

  // Handle tool calls with triple-gate security.
  //
  // WRAP-WITH-RECEIPTS fold point (dependency-sovereignty-ladder, 2026-07-16):
  // every HTTP/SSE tool call routes through gateToolCall — one NDJSON receipt
  // per call (sha256 of canonical-JSON args, NEVER raw args) plus the typed
  // seam where FounderGate / x402 / envelope-validation checks plug in. The
  // seam now runs the REAL check (tool-call-checks.ts): exact-four/prohibited
  // authority routing on the tool NAME, active @frame_declaration enforcement,
  // and the same pure Gate-2 scope authorization the downstream triple gate
  // re-runs. A denial throws
  // ToolCallGateDeniedError pre-dispatch (receipt written first, deniedBy
  // names the branch). The triple gate + audit log keep running DOWNSTREAM
  // inside securedToolExecution, which never throws (failures come back as
  // isError, which the classifier maps onto the receipt's error status).
  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;
    const frameDeclaration = frameDeclarationFromMcpMeta(
      (request.params as { _meta?: unknown })._meta
    );

    // Use session auth context or default to admin (for legacy compat)
    const auth: TokenIntrospection = sessionAuthContext || {
      active: true,
      scopes: ['admin:*'],
      agentId: 'mcp-session-legacy',
    };

    const { result, isError } = await gateToolCall(
      { name, args: args || {} },
      {
        transport: 'http',
        callerId: auth.agentId ?? auth.clientId ?? null,
        scopes: auth.scopes,
        ...(frameDeclaration !== undefined ? { frameDeclaration } : {}),
      },
      (env) => securedToolExecution(env.name, env.args, auth),
      {
        check: founderGateX402ToolCallCheck,
        classifyResult: (r) =>
          r.isError ? { ok: false, errorClass: 'ToolExecutionError' } : { ok: true },
      }
    );

    // Hologram MCP envelope detection (task_1778114362909_zp7u): if the tool
    // returned a HologramMcpResponse, surface the typed channel so REST/MCP
    // clients can dispatch on content_type without re-parsing JSON.
    if (!isError && isHologramMcpResponse(result)) {
      return wrapHologramMcpEnvelope(result) as unknown as Record<string, unknown>;
    }

    return {
      content: [
        {
          type: 'text',
          text: typeof result === 'string' ? result : JSON.stringify(result, null, 2),
        },
      ],
      ...(isError ? { isError: true } : {}),
    };
  });

  return server;
}

/**
 * Unified tool handler for A2A task execution.
 * Routes through triple-gate security, plugins, then the main handler pipeline.
 */
async function handleToolForA2A(name: string, args: Record<string, unknown>): Promise<unknown> {
  // A2A tasks get admin scope (they're already authenticated at the HTTP layer)
  const auth: TokenIntrospection = {
    active: true,
    scopes: ['admin:*'],
    agentId: 'a2a-task-executor',
  };

  const { result, isError } = await securedToolExecution(name, args, auth, {
    requestPath: '/a2a/tasks',
    requestMethod: 'POST',
  });

  if (isError) {
    const detail = typeof result === 'string' ? result : JSON.stringify(result).slice(0, 300);
    throw new Error(
      `[A2A task dispatch] Tool execution failed: ${detail}. Check the tool handler and input parameters.`
    );
  }
  return result;
}

// ── HTTP Server ──────────────────────────────────────────────────────────────

const httpServer = http.createServer(async (req, res) => {
  // CORS headers (extended for OAuth 2.1)
  // Default to wildcard for MCP/agent clients; restrict via CORS_ALLOWED_ORIGINS env var
  // in deployments that serve browser-based Studio traffic alongside the MCP endpoint.
  const allowedOrigin = process.env.CORS_ALLOWED_ORIGINS ?? '*';
  const requestOrigin = req.headers['origin'];
  if (allowedOrigin !== '*' && requestOrigin) {
    const allowed = allowedOrigin.split(',').map((o) => o.trim());
    res.setHeader(
      'Access-Control-Allow-Origin',
      allowed.includes(requestOrigin) ? requestOrigin : allowed[0]
    );
    res.setHeader('Vary', 'Origin');
  } else {
    res.setHeader('Access-Control-Allow-Origin', '*');
  }
  res.setHeader('Access-Control-Allow-Methods', 'GET, POST, DELETE, OPTIONS');
  res.setHeader(
    'Access-Control-Allow-Headers',
    'Content-Type, Authorization, x-api-key, Mcp-Session-Id, DPoP'
  );
  res.setHeader('Access-Control-Expose-Headers', 'Mcp-Session-Id, WWW-Authenticate');

  if (req.method === 'OPTIONS') {
    res.writeHead(200);
    res.end();
    return;
  }

  // Pathname for route matching (no query string); fullUrl preserves the
  // query so sub-route handlers can parse pagination/filter params. Bug
  // surfaced 2026-04-24: GET /board/done?limit=5&offset=30 was being
  // handled with empty query because handleHoloMeshRoute was receiving
  // the query-stripped path. parseQuery(url) inside the handler then
  // returned an empty URLSearchParams and pinned defaults regardless of
  // the request. Sub-route handlers do their own pathname extraction via
  // `new URL(url, 'http://localhost').pathname`, so passing the full URL
  // is strictly more correct than passing the bare path.
  const url = req.url?.split('?')[0];
  const fullUrl = req.url || url || '';
  const clientIP = getClientIP(req);

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC ENDPOINTS (no authentication required)
  // ═══════════════════════════════════════════════════════════════════════════

  // Health check (unauthenticated for Railway)
  if (url === '/health') {
    const oauthStats = oauth.getStats();
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(
      JSON.stringify({
        status: 'healthy',
        service: SERVICE_NAME,
        version: SERVICE_VERSION,
        uptime: process.uptime(),
        sessions: transports.size,
        tools: tools.length + PluginManager.getTools().length,
        // W.131 follow-up: surface team-store backend so external probes can
        // diagnose Pattern Gamma (in-memory + ephemeral container = team state
        // wiped on every redeploy) without code-spelunking. teamStore.usesPostgres
        // reflects whether DATABASE_URL was set at boot AND pg loaded successfully.
        storage: {
          databaseUrl: Boolean(process.env.DATABASE_URL),
          teamStore: teamStore.usesPostgres ? 'postgres' : 'memory',
          playerStore: playerStore.usesPostgres ? 'postgres' : 'memory',
          inviteStore: inviteStore.usesPostgres ? 'postgres' : 'memory',
          geoAnchorStore: geoAnchorStore.usesPostgres ? 'postgres' : 'memory',
          stateStore: (stateStore as { usesPostgres?: boolean }).usesPostgres
            ? 'postgres'
            : 'memory',
        },
        sse: {
          railway: IS_RAILWAY,
          mcpClassicSse: ALLOW_SSE_TRANSPORT,
          mcpStreamableHttp: true,
          holomeshTeamRoom:
            'GET /api/holomesh/team/:teamId/room/live — single long-lived connection (edge-safe headers)',
        },
        security: {
          oauth21: true,
          tripleGate: true,
          auditLogging: true,
          euAiActCompliance: true,
          registeredClients: oauthStats.registeredClients,
          activeTokens: oauthStats.activeAccessTokens,
        },
        keepAlive: getKeepAliveStatus(),
        sizing: {
          profile: SERVER_SIZING.profile,
          useCase: SERVER_SIZING.useCase,
          recommendedConsumer: SERVER_SIZING.recommendedConsumer,
          transport: SERVER_SIZING.transport,
          requestBodyMaxBytes: SERVER_SIZING.requestBodyMaxBytes,
          postgresPoolMax: SERVER_SIZING.postgresPoolMax,
          maxConcurrentToolCalls: SERVER_SIZING.maxConcurrentToolCalls,
          toolTimeoutMs: SERVER_SIZING.toolTimeoutMs,
          cacheMaxEntries: SERVER_SIZING.cacheMaxEntries,
          memoryBudgetMb: SERVER_SIZING.memoryBudgetMb,
          rateLimits: {
            oauthPerMinute: RATE_LIMIT,
            publicAnonPerMinute: PUBLIC_ANON_RATE_LIMIT,
            consumerGenerationPerMinute: CONSUMER_GEN_RATE_LIMIT,
            consumerGenerationDailyQuota: CONSUMER_GEN_DAILY_QUOTA,
          },
        },
      })
    );
    return;
  }

  // Extended health check with render capabilities
  if (url === '/api/health') {
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(
      JSON.stringify({
        status: 'healthy',
        service: SERVICE_NAME,
        version: SERVICE_VERSION,
        uptime: process.uptime(),
        capabilities: ['render', 'share', 'mcp', 'oauth21', 'audit'],
        render_url: process.env.RAILWAY_PUBLIC_DOMAIN
          ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
          : `http://localhost:${PORT}`,
        sizing: {
          profile: SERVER_SIZING.profile,
          useCase: SERVER_SIZING.useCase,
          recommendedConsumer: SERVER_SIZING.recommendedConsumer,
          transport: SERVER_SIZING.transport,
          requestBodyMaxBytes: SERVER_SIZING.requestBodyMaxBytes,
          postgresPoolMax: SERVER_SIZING.postgresPoolMax,
          maxConcurrentToolCalls: SERVER_SIZING.maxConcurrentToolCalls,
          toolTimeoutMs: SERVER_SIZING.toolTimeoutMs,
          cacheMaxEntries: SERVER_SIZING.cacheMaxEntries,
          memoryBudgetMb: SERVER_SIZING.memoryBudgetMb,
        },
      })
    );
    return;
  }

  // ── Public CAEL trace verification (external-repro path) ──────────────────
  // solve_* results emit verifyUrl .../verify-cael?traceId=... — this endpoint
  // makes that URL resolve instead of 404ing (the dead-URL honesty bug). The
  // in-memory trace registry is per-process/ephemeral, so GET-by-traceId is
  // best-effort; the canonical stateless path is POST { traceJSONL } with the
  // self-contained receipt embedded in the solve_* response. Intentionally
  // unauthenticated — independent third-party verification is the whole point
  // (closes the TVCG external-repro "no public HTTP path" caveat).
  if (url === '/verify-cael') {
    const rl = getRateLimit(`anon:${clientIP}`, PUBLIC_ANON_RATE_LIMIT);
    setRateLimitHeaders(res, rl);
    if (rl.remaining === 0) {
      res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'rate_limit_exceeded',
          message: `Verification endpoint limited to ${PUBLIC_ANON_RATE_LIMIT} requests/minute per IP.`,
          retry_after_seconds: Math.max(1, Math.ceil((rl.resetAt - Date.now()) / 1000)),
        })
      );
      return;
    }
    const wantsHtml = req.method === 'GET' && (req.headers.accept || '').includes('text/html');
    try {
      let traceId: string | null = null;
      let traceJSONL: string | null = null;
      if (req.method === 'POST') {
        const body = await parseJsonBody(req);
        traceId = typeof body.traceId === 'string' ? body.traceId : null;
        traceJSONL = typeof body.traceJSONL === 'string' ? body.traceJSONL : null;
      } else if (req.method === 'GET') {
        const qs = new URLSearchParams(req.url?.split('?')[1] || '');
        traceId = qs.get('traceId');
      } else {
        res.writeHead(405, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Use GET ?traceId=... or POST { traceJSONL }' }));
        return;
      }

      const out = await verifyCaelResult({ traceId, traceJSONL, wantsHtml });
      res.writeHead(out.status, { 'Content-Type': out.contentType });
      res.end(out.body);
    } catch (error) {
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: error instanceof Error ? error.message : String(error) }));
    }
    return;
  }

  if (await handleSovereign3DRoute(req, res, fullUrl)) {
    return;
  }

  // HoloKey: migrate named env secrets INTO the vault (owner-scoped, idempotent). Founder-
  // auth-guarded; names-only response. The Phase 2 "set once" step, run inside the service so
  // each value reads from its own env and never transits a wire as plaintext (the encrypted
  // store is the only place it lands). Safe to call repeatedly — already-stored keys are skipped.
  if (url === '/api/holokey/migrate-env' && req.method === 'POST') {
    const provided =
      (req.headers['x-mcp-api-key'] as string | undefined) ??
      (req.headers['authorization'] as string | undefined)?.replace(/^Bearer\s+/i, '');
    if (!HOLOSCRIPT_API_KEY || provided !== HOLOSCRIPT_API_KEY) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'unauthorized' }));
      return;
    }
    const result = await migrateEnvKeys([
      'ANTHROPIC_API_KEY',
      'OPENAI_API_KEY',
      'OPENROUTER_API_KEY',
      'HOLOSCRIPT_API_KEY',
    ]);
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(result));
    return;
  }

  // Partner SDK registry credential validation. This is the concrete umbrella
  // route behind the generated RegistryClient contract: clients send a HoloKey
  // through X-API-Key (or bearer compatibility), and the response preserves the
  // generated SDK ApiResponse<T> envelope. HoloGate remains docs umbrella
  // language only; this route proves HoloKey/x402 + UmbrellaRoute behavior.
  if (handlePartnerRegistryValidateRoute(req, res, HOLOSCRIPT_API_KEY)) {
    return;
  }

  // LLM provider health probe — confirms credit status for Anthropic and
  // availability of other configured providers (task_1778462298192_564w).
  if (url === '/api/health/llm' && req.method === 'GET') {
    const providers: Record<
      string,
      { configured: boolean; creditStatus?: string; error?: string }
    > = {};
    // Anthropic probe — key resolved from the HoloKey vault if present, else process.env
    // (Phase 2 first cutover; fallback-safe — identical behavior until the key is in the vault).
    const anthropicKey = await resolveServiceSecret('ANTHROPIC_API_KEY');
    if (anthropicKey) {
      try {
        const probe = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'x-api-key': anthropicKey,
            'anthropic-version': '2023-06-01',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'claude-haiku-4-5',
            max_tokens: 1,
            messages: [{ role: 'user', content: 'hi' }],
          }),
        });
        if (probe.status === 400) {
          const body = await probe.json().catch(() => ({}));
          const msg = body.error?.message ?? '';
          if (msg.toLowerCase().includes('credit balance is too low')) {
            providers.anthropic = { configured: true, creditStatus: 'exhausted', error: msg };
          } else {
            providers.anthropic = { configured: true, creditStatus: 'degraded', error: msg };
          }
        } else if (probe.ok) {
          providers.anthropic = { configured: true, creditStatus: 'ok' };
        } else {
          providers.anthropic = {
            configured: true,
            creditStatus: 'unknown',
            error: `HTTP ${probe.status}`,
          };
        }
      } catch (e: unknown) {
        providers.anthropic = {
          configured: true,
          creditStatus: 'unreachable',
          error: e instanceof Error ? e.message : String(e),
        };
      }
    } else {
      providers.anthropic = { configured: false };
    }
    // Other providers (config status only) — keys resolved from the HoloKey vault if present,
    // else process.env (Phase 2 rollout; fallback-safe — identical until the key is in the vault).
    providers.openrouter = {
      configured: Boolean(await resolveServiceSecret('OPENROUTER_API_KEY')),
    };
    providers.openai = { configured: Boolean(await resolveServiceSecret('OPENAI_API_KEY')) };
    providers.gemini = {
      configured: Boolean(
        (await resolveServiceSecret('GEMINI_API_KEY')) || process.env.GOOGLE_AI_API_KEY
      ),
    };
    providers.ollama = { configured: Boolean(process.env.HOLOSCRIPT_LOCAL_LLM_URL) };

    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify({ status: 'healthy', providers }));
    return;
  }

  // Ops Prometheus scrape (P.008.02 — Grafana-friendly secured-tool counters + latency gauges)
  if (url === '/ops/metrics') {
    const { handleOpsMetricsRequest } = await import('./ops/tool-ops-metrics.js');
    handleOpsMetricsRequest(req, res);
    return;
  }

  if (url === '/ops/status') {
    const { handleOpsStatusRequest } = await import('./ops/tool-ops-status.js');
    await handleOpsStatusRequest(req, res);
    return;
  }

  if (url === '/ops/lb-weights') {
    const { handleLbWeightsRequest } = await import('./ops/predictive-cloudflare-lb.js');
    handleLbWeightsRequest(req, res);
    return;
  }

  // Prometheus metrics endpoint (v5.6 Observable Platform)
  if (url === '/metrics') {
    const { handleMetricsRequest } = await import('./health-check');
    handleMetricsRequest(req, res);
    return;
  }

  // .well-known/mcp discovery endpoint (MCP specification)
  if (url === '/.well-known/mcp' || url === '/.well-known/mcp.json') {
    const allTools = [...tools, ...PluginManager.getTools()];
    const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
      ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
      : `http://localhost:${PORT}`;

    res.writeHead(200, {
      'Content-Type': 'application/json; charset=utf-8',
      'Cache-Control': 'public, max-age=3600',
    });
    res.end(
      JSON.stringify({
        mcpVersion: '2025-03-26',
        name: SERVICE_NAME,
        version: SERVICE_VERSION,
        description:
          'HoloScript language tooling — parse, validate, compile, and render .hs/.hsplus/.holo compositions across 27 backend targets.',
        transport: {
          type: ALLOW_SSE_TRANSPORT ? 'sse' : 'streamable-http',
          url: `${baseUrl}/mcp`,
          authentication: {
            type: 'oauth2',
            flows: ['authorization_code', 'client_credentials'],
            authorizationEndpoint: `${baseUrl}/oauth/authorize`,
            tokenEndpoint: `${baseUrl}/oauth/token`,
            registrationEndpoint: `${baseUrl}/oauth/register`,
            introspectionEndpoint: `${baseUrl}/oauth/introspect`,
            scopes: Object.keys(OAUTH2_SCOPES),
            legacyScopes: Object.keys(SCOPE_CATEGORIES),
            legacyBearerSupported: true,
          },
        },
        capabilities: {
          tools: { count: allTools.length },
          resources: { count: listSkillResources().length },
          prompts: false,
        },
        categories: Object.entries(
          allTools.reduce(
            (acc, t) => {
              const tags = deriveSkillTags(t.name);
              const cat = CATEGORY_LABELS[tags[1] || 'utility'] || tags[1] || 'utility';
              acc[cat] = (acc[cat] || 0) + 1;
              return acc;
            },
            {} as Record<string, number>
          )
        ).sort(([, a], [, b]) => (b as number) - (a as number)),
        tools: allTools.map((t) => {
          const tags = deriveSkillTags(t.name);
          const category = tags[1] || 'utility';
          return {
            name: t.name,
            description: t.description,
            category: CATEGORY_LABELS[category] || category,
            tags: tags.slice(1),
          };
        }),
        endpoints: {
          mcp: `${baseUrl}/mcp`,
          health: `${baseUrl}/health`,
          render: `${baseUrl}/api/render`,
          share: `${baseUrl}/api/share`,
          a2a: `${baseUrl}/a2a`,
          agentCard: `${baseUrl}/.well-known/agent-card.json`,
          oauth: {
            openidConfiguration: `${baseUrl}/.well-known/openid-configuration`,
            authorize: `${baseUrl}/oauth/authorize`,
            token: `${baseUrl}/oauth/token`,
            register: `${baseUrl}/oauth/register`,
            revoke: `${baseUrl}/oauth/revoke`,
            introspect: `${baseUrl}/oauth/introspect`,
          },
          audit: `${baseUrl}/api/audit`,
        },
        contact: {
          repository: 'https://github.com/brianonbased-dev/HoloScript',
        },
      })
    );
    return;
  }

  // OpenID Configuration / OAuth Authorization Server Metadata (RFC 8414)
  if (
    url === '/.well-known/openid-configuration' ||
    url === '/.well-known/oauth-authorization-server'
  ) {
    res.writeHead(200, {
      'Content-Type': 'application/json; charset=utf-8',
      'Cache-Control': 'public, max-age=3600',
    });
    res.end(JSON.stringify(oauth.getOpenIDConfiguration(), null, 2));
    return;
  }

  // GET /.well-known/agent-card.json / agent.json — A2A Agent Card discovery
  if (
    (url === '/.well-known/agent-card.json' ||
      url === '/.well-known/agent-card' ||
      url === '/.well-known/agent.json') &&
    req.method === 'GET'
  ) {
    const allTools = [...tools, ...PluginManager.getTools()];
    const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
      ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
      : `http://localhost:${PORT}`;

    const card = buildAgentCard(allTools, baseUrl, !!HOLOSCRIPT_API_KEY);
    res.writeHead(200, {
      'Content-Type': 'application/json; charset=utf-8',
      'Cache-Control': 'public, max-age=3600',
    });
    res.end(JSON.stringify(card, null, 2));
    return;
  }

  // GET /.well-known/agent-card-lite.json — Lightweight agent card (~2KB)
  // For agents with small context windows that can't handle the full 365KB card
  if (url === '/.well-known/agent-card-lite.json' && req.method === 'GET') {
    const allTools = [...tools, ...PluginManager.getTools()];
    const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
      ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
      : `http://localhost:${PORT}`;

    // Group tools by category, just names — no schemas
    const categories: Record<string, string[]> = {};
    for (const t of allTools) {
      const cat = (t as { category?: string }).category || 'Utility';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(t.name);
    }

    const lite = {
      name: 'holoscript-mcp',
      version: '6.0.4',
      description: 'HoloScript spatial computing — parse, compile, simulate, deploy.',
      url: baseUrl,
      transport: 'streamable-http',
      endpoints: {
        mcp: `${baseUrl}/mcp`,
        health: `${baseUrl}/health`,
        a2a: `${baseUrl}/a2a`,
        quickstart: `${baseUrl}/api/holomesh/quickstart`,
        full_card: `${baseUrl}/.well-known/agent-card.json`,
      },
      auth: {
        type: 'oauth2',
        register: `${baseUrl}/oauth/register`,
        free_tools: ['parse_hs', 'parse_holo', 'health'],
      },
      tools: {
        total: allTools.length,
        categories: Object.fromEntries(
          Object.entries(categories).map(([cat, names]) => [cat, names.length])
        ),
      },
    };

    res.writeHead(200, {
      'Content-Type': 'application/json; charset=utf-8',
      'Cache-Control': 'public, max-age=3600',
    });
    res.end(JSON.stringify(lite, null, 2));
    return;
  }

  // GET /.well-known/crdt-state — Export initial CRDT state for gossip sync
  if (url === '/.well-known/crdt-state' && req.method === 'GET') {
    try {
      const worldStatePath = process.env.HOLOMESH_WORLD_STATE_PATH || './.holomesh/worldstate.crdt';
      const agentId = process.env.HOLOMESH_AGENT_ID || 'anon';
      const worldState = new HoloMeshWorldState(agentId, { snapshotPath: worldStatePath });
      const delta = worldState.exportDelta();

      res.writeHead(200, { 'Content-Type': 'application/octet-stream' });
      res.end(Buffer.from(delta));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /.well-known/crdt-gossip — Handle inbound CRDT state updates
  if (url === '/.well-known/crdt-gossip' && req.method === 'POST') {
    try {
      const body = (await parseJsonBody(req)) as unknown as GossipDeltaRequest;
      const worldStatePath = process.env.HOLOMESH_WORLD_STATE_PATH || './.holomesh/worldstate.crdt';
      const peerStorePath = process.env.HOLOMESH_PEER_STORE_PATH || './.holomesh/peers.json';
      const agentId = process.env.HOLOMESH_AGENT_ID || 'anon';
      const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
        ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
        : `http://localhost:${PORT}`;

      const worldState = new HoloMeshWorldState(agentId, { snapshotPath: worldStatePath });
      const discovery = new HoloMeshDiscovery(agentId, baseUrl, worldState, {
        storePath: peerStorePath,
      });

      const response = await handleInboundGossip(body, worldState, discovery);
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(response));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ success: false, error: message }));
    }
    return;
  }

  // GET /a2a — A2A protocol info / discovery redirect
  if (url === '/a2a' && req.method === 'GET') {
    const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
      ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
      : `http://localhost:${PORT}`;

    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(
      JSON.stringify(
        {
          protocol: 'a2a',
          version: '1.0.0',
          transport: 'json-rpc-2.0',
          agentCard: `${baseUrl}/.well-known/agent-card.json`,
          endpoints: {
            jsonrpc: `${baseUrl}/a2a`,
            tasks: `${baseUrl}/a2a/tasks`,
            agentCard: `${baseUrl}/.well-known/agent-card.json`,
          },
          methods: [
            'a2a.sendMessage',
            'a2a.getTask',
            'a2a.listTasks',
            'a2a.cancelTask',
            'a2a.getExtendedAgentCard',
          ],
          description:
            'HoloScript A2A (Agent-to-Agent) protocol endpoint. ' +
            'POST JSON-RPC 2.0 requests to this URL, or use the REST endpoints under /a2a/tasks. ' +
            'See agent card for full capabilities.',
        },
        null,
        2
      )
    );
    return;
  }

  // POST /a2a — A2A JSON-RPC 2.0 transport (per A2A specification)
  if (url === '/a2a' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);

      // Validate JSON-RPC 2.0 envelope
      const parsed = parseJsonRpcRequest(body);
      if ('error' in parsed) {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify(parsed.error));
        return;
      }

      // Build agent card builder for a2a.getExtendedAgentCard
      const agentCardBuilder = (): AgentCard => {
        const allTools = [...tools, ...PluginManager.getTools()];
        const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
          ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
          : `http://localhost:${PORT}`;
        return buildAgentCard(allTools, baseUrl, !!HOLOSCRIPT_API_KEY);
      };

      // Handle the JSON-RPC request
      const response = await handleJsonRpcRequest(
        parsed.request,
        handleToolForA2A,
        agentCardBuilder
      );

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(response, null, 2));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // JSON-RPC spec: parse errors return -32700
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          jsonrpc: '2.0',
          id: null,
          error: {
            code: -32700,
            message: `Parse error: ${message}`,
          },
        })
      );
    }
    return;
  }

  // Scene serving (public, read-only)
  // Supports ?renderer=webgpu to serve the sovereign WebGPU path instead of
  // the Three.js bridge (task h7yz — wire WebGPURenderer into preview/deploy).
  const sceneMatch = url?.match(/^\/scene\/([a-f0-9]{8})$/);
  if (sceneMatch && req.method === 'GET') {
    const scene = getScene(sceneMatch[1]);
    if (!scene) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Scene not found' }));
      return;
    }
    const useWebGPU =
      new URLSearchParams(req.url?.split('?')[1] ?? '').get('renderer') === 'webgpu';
    if (useWebGPU) {
      try {
        const compiled = await handleCompileToTarget({
          code: scene.code,
          target: 'webgpu',
          options: {},
        });
        if (compiled.success && compiled.previewHtml) {
          res.writeHead(200, {
            'Content-Type': 'text/html; charset=utf-8',
            'X-HoloScript-Renderer': 'sovereign-webgpu',
          });
          res.end(compiled.previewHtml);
          return;
        }
      } catch (_) {
        /* fall through to Three.js bridge on compile error */
      }
    }
    res.writeHead(200, { 'Content-Type': 'text/html; charset=utf-8' });
    res.end(generateBrowserTemplate(scene.code, scene.title, scene.id));
    return;
  }

  const embedMatch = url?.match(/^\/embed\/([a-f0-9]{8})$/);
  if (embedMatch && req.method === 'GET') {
    const scene = getScene(embedMatch[1]);
    if (!scene) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Scene not found' }));
      return;
    }
    const useWebGPU =
      new URLSearchParams(req.url?.split('?')[1] ?? '').get('renderer') === 'webgpu';
    if (useWebGPU) {
      try {
        const compiled = await handleCompileToTarget({
          code: scene.code,
          target: 'webgpu',
          options: {},
        });
        if (compiled.success && compiled.previewHtml) {
          res.writeHead(200, {
            'Content-Type': 'text/html; charset=utf-8',
            'X-Frame-Options': 'ALLOWALL',
            'X-HoloScript-Renderer': 'sovereign-webgpu',
          });
          res.end(compiled.previewHtml);
          return;
        }
      } catch (_) {
        /* fall through to Three.js bridge on compile error */
      }
    }
    res.writeHead(200, {
      'Content-Type': 'text/html; charset=utf-8',
      'X-Frame-Options': 'ALLOWALL',
    });
    res.end(generateBrowserTemplate(scene.code, scene.title, scene.id));
    return;
  }

  // GET /api/scene/:id/thumbnail — serve PNG thumbnail for social previews
  const thumbnailMatch = url?.match(/^\/api\/scene\/([a-f0-9]{8})\/thumbnail$/);
  if (thumbnailMatch && req.method === 'GET') {
    const sceneId = thumbnailMatch[1];
    const scene = getScene(sceneId);
    if (!scene) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Scene not found' }));
      return;
    }

    // Try cached thumbnail first
    let buffer = getThumbnail(sceneId);
    if (!buffer) {
      // Generate on-demand (blocks this request but caches for future)
      buffer = await generateThumbnail(sceneId);
    }

    if (buffer) {
      res.writeHead(200, {
        'Content-Type': 'image/png',
        'Cache-Control': 'public, max-age=86400',
        'Content-Length': buffer.length.toString(),
      });
      res.end(buffer);
    } else {
      // Fallback: redirect to a placeholder or return 202
      res.writeHead(202, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ status: 'generating', retry_after: 3 }));
    }
    return;
  }

  // GET /api/hologram/:hash/:artifact — serve content-addressed hologram bundle artifacts
  // Artifacts: preview.png, quilt.png, left-eye.png, right-eye.png, stereo-preview.mp4, manifest.json, scene.holo
  const hologramArtifactMatch = url?.match(/^\/api\/hologram\/([a-f0-9]{64})\/([\w.-]+)$/);
  if (hologramArtifactMatch && req.method === 'GET') {
    const bundleHash = hologramArtifactMatch[1];
    const artifactName = hologramArtifactMatch[2];

    const ALLOWED_ARTIFACTS: Record<string, string> = {
      'preview.png': 'image/png',
      'quilt.png': 'image/png',
      'left-eye.png': 'image/png',
      'right-eye.png': 'image/png',
      'stereo-preview.mp4': 'video/mp4',
      'manifest.json': 'application/json',
      'scene.holo': 'text/plain; charset=utf-8',
    };

    const mimeType = ALLOWED_ARTIFACTS[artifactName];
    if (!mimeType) {
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({ error: 'Unknown artifact', allowed: Object.keys(ALLOWED_ARTIFACTS) })
      );
      return;
    }

    const artifactPath = pathJoin(resolveStoreRoot(), bundleHash, artifactName);
    try {
      const data = await fsPromises.readFile(artifactPath);
      const cacheControl =
        mimeType.startsWith('image/') || mimeType === 'video/mp4'
          ? 'public, max-age=86400, immutable'
          : 'public, max-age=3600';
      res.writeHead(200, {
        'Content-Type': mimeType,
        'Content-Length': data.length.toString(),
        'Cache-Control': cacheControl,
        'X-Bundle-Hash': bundleHash,
      });
      res.end(data);
    } catch (err) {
      const code = (err as NodeJS.ErrnoException).code;
      if (code === 'ENOENT') {
        res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({ error: 'Artifact not found', hash: bundleHash, artifact: artifactName })
        );
      } else {
        const message = err instanceof Error ? err.message : String(err);
        res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Failed to read artifact', detail: message }));
      }
    }
    return;
  }

  const apiSceneMatch = url?.match(/^\/api\/scene\/([a-f0-9]{8})$/);
  if (apiSceneMatch && req.method === 'GET') {
    const scene = getScene(apiSceneMatch[1]);
    if (!scene) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Scene not found' }));
      return;
    }
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(scene));
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OAUTH 2.1 ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  // POST /oauth/register — Dynamic client registration (dual-register to both providers)
  if (url === '/oauth/register' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const clientName = (body.client_name as string) || 'unnamed-client';
      const redirectUris = (body.redirect_uris as string[]) || [];
      const scopes = (body.scope as string)?.split(' ') || ['tools:read'];
      const clientType = (
        body.token_endpoint_auth_method === 'none' ? 'public' : 'confidential'
      ) as 'confidential' | 'public';
      const rateLimit = (body.rate_limit as number) || 60;

      // Register with legacy provider (backwards compat)
      const { clientId, clientSecret } = oauth.registerClient({
        clientName,
        redirectUris,
        scopes,
        clientType,
        rateLimit,
      });

      // Also register with the new OAuth2Provider (token-store backed) using
      // the SAME identity, so the durable copy is reachable by the credentials
      // the caller holds. The previous divergent dual-write (separate generated
      // ids) made the Postgres copy useless for rehydration after deploys.
      try {
        await oauth2.registerClient({
          clientName,
          redirectUris,
          scopes,
          clientType,
          rateLimit,
          clientId,
          clientSecret,
        });
      } catch (oauth2Err) {
        console.warn(
          '[auth] OAuth2Provider registration failed; legacy registry succeeded:',
          oauth2Err instanceof Error ? oauth2Err.message : oauth2Err
        );
      }

      auditLog.logAuthEvent({
        event: 'client_registered',
        clientId,
        ip: clientIP,
      });

      res.writeHead(201, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          client_id: clientId,
          client_secret: clientSecret,
          client_name: body.client_name,
          redirect_uris: redirectUris,
          token_endpoint_auth_method: 'client_secret_post',
          grant_types: ['authorization_code', 'client_credentials', 'refresh_token'],
          response_types: ['code'],
          scope: (body.scope as string) || 'tools:read',
          scopes_supported: Object.keys(OAUTH2_SCOPES),
          client_id_issued_at: Math.floor(Date.now() / 1000),
          client_secret_expires_at: 0,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'invalid_client_metadata', error_description: message }));
    }
    return;
  }

  // GET /oauth/authorize — Authorization request (returns consent info / validates params)
  if (url?.startsWith('/oauth/authorize') && req.method === 'GET') {
    try {
      const queryString = req.url?.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const host = req.headers.host || `localhost:${PORT}`;
      const protocol = req.headers['x-forwarded-proto'] || 'http';
      const baseUrl = `${protocol}://${host}`;
      const responseType = params.get('response_type');
      const clientId = params.get('client_id');
      const redirectUri = params.get('redirect_uri');
      const scope = params.get('scope') || 'tools:read';
      const state = params.get('state');
      const codeChallenge = params.get('code_challenge');
      const codeChallengeMethod = params.get('code_challenge_method');

      if (responseType !== 'code') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'unsupported_response_type',
              error_description: 'Only response_type=code is supported (OAuth 2.1)',
            },
            null,
            2
          )
        );
        return;
      }

      if (!clientId) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'invalid_request',
              error_description: 'Missing required parameter: client_id',
            },
            null,
            2
          )
        );
        return;
      }

      if (!redirectUri) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'invalid_request',
              error_description: 'Missing required parameter: redirect_uri',
            },
            null,
            2
          )
        );
        return;
      }

      if (!codeChallenge || codeChallengeMethod !== 'S256') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'invalid_request',
              error_description:
                'PKCE is mandatory. Provide code_challenge with code_challenge_method=S256',
            },
            null,
            2
          )
        );
        return;
      }

      // Validate against the same registry used by POST /oauth/register and POST /oauth/token.
      // Rehydrate from the durable registry first — the in-memory one is wiped on deploys.
      await ensureClientHydrated(clientId);
      const client = oauth.getClient(clientId);
      if (!client) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'invalid_client',
              error_description: 'Unknown client_id. Register via POST /oauth/register first.',
            },
            null,
            2
          )
        );
        return;
      }

      if (!client.redirectUris.includes(redirectUri)) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'invalid_request',
              error_description: `redirect_uri "${redirectUri}" is not registered for this client`,
            },
            null,
            2
          )
        );
        return;
      }

      const requestedScopes = scope.split(' ').filter(Boolean);
      const invalidScopes = requestedScopes.filter(
        (requestedScope) =>
          !client.scopes.includes(requestedScope) && !client.scopes.includes('admin')
      );
      if (invalidScopes.length > 0) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify(
            {
              error: 'invalid_scope',
              error_description: `Scopes not authorized for client: ${invalidScopes.join(', ')}`,
            },
            null,
            2
          )
        );
        return;
      }

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify(
          {
            authorization_request: {
              client_id: clientId,
              client_name: client.clientName,
              redirect_uri: redirectUri,
              scope: requestedScopes.join(' '),
              state: state || undefined,
              code_challenge: codeChallenge,
              code_challenge_method: 'S256',
              available_scopes: Object.entries(OAUTH2_SCOPES).map(([name, description]) => ({
                name,
                description,
                requested: requestedScopes.includes(name),
              })),
            },
            instructions:
              'POST to /oauth/authorize with the same parameters to obtain an authorization code.',
            _links: {
              authorize: `${baseUrl}/oauth/authorize`,
              token: `${baseUrl}/oauth/token`,
              register: `${baseUrl}/oauth/register`,
            },
          },
          null,
          2
        )
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'invalid_request', error_description: message }));
    }
    return;
  }

  // POST /oauth/authorize — Authorization code (with PKCE)
  if (url === '/oauth/authorize' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const code = oauth.createAuthorizationCode({
        clientId: body.client_id as string,
        redirectUri: body.redirect_uri as string,
        scopes: ((body.scope as string) || '').split(' ').filter(Boolean),
        codeChallenge: body.code_challenge as string,
        codeChallengeMethod: 'S256',
      });

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          code,
          state: body.state || undefined,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'invalid_request', error_description: message }));
    }
    return;
  }

  // POST /oauth/token — Token exchange
  if (url === '/oauth/token' && req.method === 'POST') {
    const rl = getRateLimit(clientIP);
    setRateLimitHeaders(res, rl);

    if (rl.remaining <= 0) {
      res.writeHead(429, {
        'Content-Type': 'application/json; charset=utf-8',
        'Retry-After': String(Math.ceil((rl.resetAt - Date.now()) / 1000)),
      });
      res.end(
        JSON.stringify({
          error: 'rate_limit_exceeded',
          error_description: 'Too many token requests. Try again later.',
        })
      );
      return;
    }

    try {
      const body = await parseJsonBody(req);
      const grantType = body.grant_type as string;
      const dpopHeader = req.headers['dpop'] as string | undefined;

      // Rehydrate durable state before the sync grant handlers consult the
      // in-memory maps (wiped on every deploy).
      await ensureClientHydrated(body.client_id as string);
      if (grantType === 'refresh_token') {
        await ensureRefreshTokenHydrated(body.refresh_token as string | undefined);
      }

      let tokenResponse;

      switch (grantType) {
        case 'authorization_code':
          tokenResponse = oauth.exchangeAuthorizationCode({
            code: body.code as string,
            clientId: body.client_id as string,
            clientSecret: body.client_secret as string,
            redirectUri: body.redirect_uri as string,
            codeVerifier: body.code_verifier as string,
            agentId: body.agent_id as string | undefined,
            dpopThumbprint: dpopHeader,
          });
          break;

        case 'client_credentials':
          tokenResponse = oauth.exchangeClientCredentials({
            clientId: body.client_id as string,
            clientSecret: body.client_secret as string,
            scopes: ((body.scope as string) || '').split(' ').filter(Boolean),
            agentId: body.agent_id as string | undefined,
            dpopThumbprint: dpopHeader,
          });
          break;

        case 'refresh_token':
          tokenResponse = oauth.refreshAccessToken({
            refreshToken: body.refresh_token as string,
            clientId: body.client_id as string,
            clientSecret: body.client_secret as string,
            dpopThumbprint: dpopHeader,
          });
          break;

        default:
          res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(
            JSON.stringify({
              error: 'unsupported_grant_type',
              error_description: `Grant type "${grantType}" is not supported. Use authorization_code, client_credentials, or refresh_token.`,
            })
          );
          return;
      }

      auditLog.logAuthEvent({
        event: 'token_issued',
        clientId: body.client_id as string,
        ip: clientIP,
      });

      // Write the issued tokens through to the durable registry so they
      // survive redeploys of the in-memory maps. Fire-and-forget: durable
      // write failure must not fail the grant.
      void persistIssuedTokens(
        tokenResponse as {
          access_token?: string;
          refresh_token?: string;
          expires_in?: number;
          scope?: string;
        },
        body.client_id as string,
        body.agent_id as string | undefined
      );

      res.writeHead(200, {
        'Content-Type': 'application/json; charset=utf-8',
        'Cache-Control': 'no-store',
        Pragma: 'no-cache',
      });
      res.end(JSON.stringify(tokenResponse));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);

      auditLog.logAuthEvent({
        event: 'auth_failure',
        ip: clientIP,
        reason: message,
      });

      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'invalid_grant', error_description: message }));
    }
    return;
  }

  // POST /oauth/revoke — Token revocation
  if (url === '/oauth/revoke' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const token = body.token as string;
      const revoked = oauth.revokeToken(token);

      if (revoked) {
        auditLog.logAuthEvent({
          event: 'token_revoked',
          ip: clientIP,
        });
      }

      // RFC 7009: Always return 200, even if token was not found
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ revoked }));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'invalid_request', error_description: message }));
    }
    return;
  }

  // POST /oauth/introspect — Token introspection
  if (url === '/oauth/introspect' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const token = body.token as string;
      let result = oauth.introspect(token);

      // Fall back to the durable registry — the in-memory maps are wiped on deploys.
      if (!result.active) {
        const durable = await oauth2.introspect(token);
        if (durable.active) {
          result = {
            active: true,
            clientId: durable.clientId,
            scopes: (durable.scopes || []) as OAuthScope[],
            agentId: durable.agentId,
            expiresAt: durable.expiresAt,
            issuedAt: durable.issuedAt,
          };
        }
      }

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          active: result.active,
          client_id: result.clientId,
          scope: result.scopes?.join(' '),
          agent_id: result.agentId,
          exp: result.expiresAt ? Math.floor(result.expiresAt / 1000) : undefined,
          iat: result.issuedAt ? Math.floor(result.issuedAt / 1000) : undefined,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'invalid_request', error_description: message }));
    }
    return;
  }

  // POST /oauth/github/device — GitHub OAuth 2.0 Device Authorization Grant (RFC 8628)
  if (url === '/oauth/github/device' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const { initiateDeviceFlow, pollDeviceFlow, exchangeForHoloMeshToken, getDeviceFlowStats } =
        await import('./security/github-device-flow.js');

      const action = body.action as string;

      if (action === 'initiate') {
        const scope = body.scope as string | undefined;
        const result = await initiateDeviceFlow(scope);
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify(result));
        return;
      }

      if (action === 'poll') {
        const deviceCode = body.device_code as string;
        if (!deviceCode) {
          res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(
            JSON.stringify({ error: 'invalid_request', error_description: 'device_code required' })
          );
          return;
        }
        const result = await pollDeviceFlow(deviceCode);
        if ('error' in result) {
          res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(JSON.stringify(result));
          return;
        }
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify(result));
        return;
      }

      if (action === 'exchange') {
        const deviceCode = body.device_code as string;
        if (!deviceCode) {
          res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(
            JSON.stringify({ error: 'invalid_request', error_description: 'device_code required' })
          );
          return;
        }
        const result = await exchangeForHoloMeshToken(deviceCode);
        if (!result) {
          res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(
            JSON.stringify({
              error: 'authorization_pending',
              error_description: 'Device flow not complete or token invalid. Poll again.',
            })
          );
          return;
        }
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify(result));
        return;
      }

      if (action === 'stats') {
        const stats = getDeviceFlowStats();
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify(stats));
        return;
      }

      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'invalid_request',
          error_description: 'Unknown action. Use initiate, poll, exchange, or stats.',
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'server_error', error_description: message }));
    }
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTHENTICATED ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  // MCP SSE endpoint
  if (url === '/mcp' && req.method === 'GET') {
    if (!ALLOW_SSE_TRANSPORT) {
      res.writeHead(409, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'SSE transport disabled',
          reason:
            'On Railway, MCP SSE is off by default: edge routing can send GET /mcp and POST /mcp/messages to different replicas so the session map misses.',
          hint: 'Prefer stateless MCP: POST /mcp (streamable HTTP). To force classic SSE here, set MCP_ENABLE_SSE=true and run a single replica (or sticky routing), and keep CDN/proxy buffering disabled for /mcp*. HoloMesh team room uses a single GET only: /api/holomesh/team/:id/room/live.',
          transport: 'streamable-http',
        })
      );
      return;
    }

    const auth = await authenticateRequest(req);
    if (!auth.active) {
      auditLog.logAuthEvent({
        event: 'auth_failure',
        ip: clientIP,
        reason: 'MCP endpoint - invalid credentials',
      });

      res.writeHead(401, {
        'Content-Type': 'application/json; charset=utf-8',
        'WWW-Authenticate': 'Bearer realm="holoscript-mcp", error="invalid_token"',
      });
      res.end(
        JSON.stringify({
          error: 'Unauthorized',
          message: 'Valid OAuth 2.1 token or API key required',
          token_endpoint: '/oauth/token',
          registration_endpoint: '/oauth/register',
        })
      );
      return;
    }

    auditLog.logAuthEvent({
      event: 'auth_success',
      clientId: auth.clientId,
      agentId: auth.agentId,
      ip: clientIP,
    });

    // Surface auth mode so agents know when they're unauthenticated
    if (auth.agentId === 'open-dev-mode') {
      res.setHeader('X-Auth-Mode', 'permissive-open');
    } else if (!auth.clientId) {
      res.setHeader('X-Auth-Mode', 'legacy-key');
    } else {
      res.setHeader('X-Auth-Mode', 'oauth2');
    }

    await createAndStoreSessionTransport(req, res, auth);
    return;
  }

  if (url === '/mcp/messages' && req.method === 'POST') {
    if (!ALLOW_SSE_TRANSPORT) {
      res.writeHead(409, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'SSE message endpoint disabled',
          hint: 'Use stateless MCP over POST /mcp, or enable MCP_ENABLE_SSE with a single MCP replica. HoloMesh: GET /api/holomesh/team/:id/room/live is unaffected.',
          transport: 'streamable-http',
        })
      );
      return;
    }

    const queryString = req.url?.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const sessionId = params.get('sessionId');

    if (!sessionId) {
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Session ID required in query parameters' }));
      return;
    }

    const transport = transports.get(sessionId);
    if (!transport) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Session not found', sessionId }));
      return;
    }

    await transport.handlePostMessage(req, res);
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // A2A TASK ENDPOINTS (authenticated)
  // ═══════════════════════════════════════════════════════════════════════════

  // POST /a2a/tasks — Send/create a task (A2A tasks/send)
  if (url === '/a2a/tasks' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);

      const message: TaskMessage = (body.message as TaskMessage) || {
        role: 'user',
        parts: [{ type: 'text', text: JSON.stringify(body) }],
        timestamp: new Date().toISOString(),
      };

      const request: SendTaskRequest = {
        id: body.id as string | undefined,
        sessionId: body.sessionId as string | undefined,
        contextId: body.contextId as string | undefined,
        message,
        skillId: body.skillId as string | undefined,
        arguments: body.arguments as Record<string, unknown> | undefined,
        metadata: {
          ...((body.metadata as Record<string, unknown>) || {}),
          ...(body.skillId ? { skillId: body.skillId } : {}),
          ...(body.arguments ? { arguments: body.arguments } : {}),
        },
      };

      const task = createTask(request);
      const executed = await executeTask(task, handleToolForA2A);

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(taskToResponse(executed), null, 2));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // GET /a2a/tasks — List tasks
  if (url === '/a2a/tasks' && req.method === 'GET') {
    const queryString = req.url?.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const filters: {
      sessionId?: string;
      contextId?: string;
      state?: TaskState;
      limit?: number;
      offset?: number;
    } = {};

    if (params.get('sessionId')) filters.sessionId = params.get('sessionId')!;
    if (params.get('contextId')) filters.contextId = params.get('contextId')!;
    if (params.get('state')) filters.state = params.get('state') as TaskState;
    if (params.get('limit')) filters.limit = parseInt(params.get('limit')!, 10);
    if (params.get('offset')) filters.offset = parseInt(params.get('offset')!, 10);

    const result = listTasks(filters);
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(
      JSON.stringify(
        {
          tasks: result.tasks.map(taskToResponse),
          total: result.total,
        },
        null,
        2
      )
    );
    return;
  }

  // GET /a2a/tasks/:id — Get a specific task
  const taskGetMatch = url?.match(/^\/a2a\/tasks\/([a-f0-9-]+)$/);
  if (taskGetMatch && req.method === 'GET') {
    const task = getTask(taskGetMatch[1]);
    if (!task) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Task not found', id: taskGetMatch[1] }));
      return;
    }
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(taskToResponse(task), null, 2));
    return;
  }

  // DELETE /a2a/tasks/:id — Cancel a task
  const taskDeleteMatch = url?.match(/^\/a2a\/tasks\/([a-f0-9-]+)$/);
  if (taskDeleteMatch && req.method === 'DELETE') {
    const task = cancelTask(taskDeleteMatch[1]);
    if (!task) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Task not found', id: taskDeleteMatch[1] }));
      return;
    }
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(taskToResponse(task), null, 2));
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CREDIT API ROUTES — used by Studio creditGate.ts
  // ═══════════════════════════════════════════════════════════════════════════

  if (url === '/api/credits/check' && req.method === 'POST') {
    // Auth required
    const authed = await checkAuth(req);
    if (!authed) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Authentication required' }));
      return;
    }
    // Founder bypass: key-registry founders always pass the credit gate.
    if (isFounderRequest(req)) {
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ ok: true, balance: 999_999_999, required: 0, founderBypass: true }));
      return;
    }
    try {
      const body = await parseJsonBody(req);
      const userId = body.userId as string;
      const operation = body.operation as string;
      if (!userId || !operation) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required fields: userId, operation' }));
        return;
      }
      const opCost = OPERATION_COSTS[operation];
      if (!opCost) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: `Unknown operation: ${operation}` }));
        return;
      }
      if (!pgPool) {
        // No DB — allow all requests (graceful degradation)
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ ok: true, balance: Infinity, required: opCost.baseCostCents }));
        return;
      }
      const result = await pgPool.query(
        'SELECT balance_cents FROM credit_accounts WHERE user_id = $1',
        [userId]
      );
      const balance = result.rows[0]?.balance_cents ?? 0;
      if (balance >= opCost.baseCostCents) {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ ok: true, balance, required: opCost.baseCostCents }));
      } else {
        res.writeHead(402, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error: 'Insufficient credits',
            balance,
            required: opCost.baseCostCents,
            description: opCost.description,
          })
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Credit check failed', detail: message }));
    }
    return;
  }

  if (url === '/api/credits/deduct' && req.method === 'POST') {
    // Auth required
    const authed = await checkAuth(req);
    if (!authed) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Authentication required' }));
      return;
    }
    // Founder bypass: no credit deduction for key-registry founders.
    if (isFounderRequest(req)) {
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ ok: true, cost: 0, founderBypass: true }));
      return;
    }
    try {
      const body = await parseJsonBody(req);
      const userId = body.userId as string;
      const operation = body.operation as string;
      if (!userId || !operation) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required fields: userId, operation' }));
        return;
      }
      const opCost = OPERATION_COSTS[operation];
      if (!opCost) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: `Unknown operation: ${operation}` }));
        return;
      }
      if (!pgPool) {
        // No DB — accept deduction silently (graceful degradation)
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ ok: true, cost: opCost.baseCostCents }));
        return;
      }
      // Atomic deduct: only succeeds if balance is sufficient
      const result = await pgPool.query(
        `UPDATE credit_accounts
         SET balance_cents = balance_cents - $1,
             lifetime_spent_cents = lifetime_spent_cents + $1,
             updated_at = NOW()
         WHERE user_id = $2 AND balance_cents >= $1
         RETURNING balance_cents`,
        [opCost.baseCostCents, userId]
      );
      if (result.rowCount === 0) {
        res.writeHead(402, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error: 'Insufficient credits',
            required: opCost.baseCostCents,
            description: opCost.description,
          })
        );
        return;
      }
      // Record transaction (fire-and-forget, don't block response)
      pgPool
        .query(
          `INSERT INTO credit_transactions (user_id, type, amount_cents, balance_after_cents, description, metadata)
         VALUES ($1, 'usage', $2, $3, $4, $5)`,
          [
            userId,
            -opCost.baseCostCents,
            result.rows[0].balance_cents,
            opCost.description,
            JSON.stringify({ operation }),
          ]
        )
        .catch((txErr: unknown) => console.error('[credits] Failed to record transaction:', txErr));
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          ok: true,
          cost: opCost.baseCostCents,
          balance: result.rows[0].balance_cents,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Credit deduction failed', detail: message }));
    }
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MOLTBOOK CROSSPOST — same contract as legacy MCP orchestrator
  // POST /api/moltbook/crosspost (HoloMesh handoffs + W/P/g knowledge rows)
  // ═══════════════════════════════════════════════════════════════════════════

  if (url === '/api/moltbook/crosspost' && req.method === 'POST') {
    const authed = await checkAuth(req);
    if (!authed) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ success: false, error: 'Authentication required' }));
      return;
    }
    // Phase-3 wrapped read: gated by `env:MOLTBOOK_API_KEY` lease when
    // HOLOMESH_VAULT_LEASE_ENFORCE is on; transparent passthrough otherwise.
    const moltKey = readMoltbookApiKeyForRoute();
    if (!moltKey) {
      res.writeHead(503, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          success: false,
          error: 'MOLTBOOK_API_KEY not configured on this MCP server',
        })
      );
      return;
    }
    try {
      const raw = await parseJsonBody(req);
      const environmentContext = raw.environmentContext;
      const flat = { ...raw };
      delete flat.environmentContext;

      let built;
      try {
        built = buildMoltbookCrosspostPayload(flat);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ success: false, error: msg }));
        return;
      }

      if (
        environmentContext &&
        typeof environmentContext === 'object' &&
        Object.keys(environmentContext as object).length > 0
      ) {
        const md = formatBroadcastContextMarkdown(environmentContext as Record<string, string>);
        if (md) built = { ...built, content: `${built.content}\n\n${md}` };
      }

      const created = await createMoltbookPost({
        apiKey: moltKey,
        title: built.title,
        content: built.content,
        submolt: built.submolt,
      });

      if (!created.success) {
        res.writeHead(502, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            success: false,
            error: 'Moltbook API rejected the post',
            details: created.details,
          })
        );
        return;
      }

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          success: true,
          route: 'mcp-server',
          timestamp: new Date().toISOString(),
          ...created.data,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({ success: false, error: 'Crosspost handler failed', detail: message })
      );
    }
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // HOLOMESH API ROUTES (delegated to separate module)
  // ═══════════════════════════════════════════════════════════════════════════

  if (url?.startsWith('/api/holomesh/')) {
    const { handleHoloMeshRoute } = await import('./holomesh/http-routes');
    const handled = await handleHoloMeshRoute(req, res, fullUrl);
    if (handled) return;
  }

  // /api/identity/* family (task_1777863437716_edgm fix). Four sub-trees share
  // this root per the v3 escape-hatch spec + seat-wallets ADR §"Attestation flow"
  // and the 2026-05-06 vault-lease addition (task_1778102013331_u8q2):
  //   - identity-export-routes:   /api/identity/export/*    (Tier-2 self-custody)
  //   - attestation-routes:       /api/identity/attestation/{pending,approve,revoke}
  //   - custodial-wallet-routes:  /api/identity/custodial/{provision,sign,wallet,...}
  //   - vault-lease-routes:       /api/identity/vault/{lease,leases,sweep,...}
  //
  // Each handler returns true if the route matched (regardless of outcome) and
  // false if the caller should keep searching. We try them in order; first
  // match wins. Without all four wired here, attestation/*, custodial/*, and
  // vault/* would return 404 even though their handler files exist (the W.131
  // dispatch-tree gotcha — http-routes.ts only handles /api/holomesh/*).
  if (url?.startsWith('/api/identity/')) {
    const pathname = new URL(url, 'http://localhost').pathname;
    const method = req.method || 'GET';

    const { handleIdentityExportRoutes } = await import('./holomesh/routes/identity-export-routes');
    if (await handleIdentityExportRoutes(req, res, pathname, method, url)) return;

    const { handleAttestationRoutes } = await import('./holomesh/routes/attestation-routes');
    if (await handleAttestationRoutes(req, res, pathname, method, url)) return;

    const { handleCustodialWalletRoutes } =
      await import('./holomesh/routes/custodial-wallet-routes');
    if (await handleCustodialWalletRoutes(req, res, pathname, method, url)) return;

    const { handleVaultLeaseRoutes } = await import('./holomesh/routes/vault-lease-routes');
    if (await handleVaultLeaseRoutes(req, res, pathname, method, url)) return;
  }

  if (url?.startsWith('/api/absorb/')) {
    const { handleAbsorbRoute } = await import('./absorb/http-routes');
    const handled = await handleAbsorbRoute(req, res, url);
    if (handled) return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REST API ENDPOINTS (public creation, authenticated where noted)
  // ═══════════════════════════════════════════════════════════════════════════

  // POST /api/compile — compile HoloScript to any target, return raw output code
  // Rate limited: 60 requests per minute per IP. Input capped at 100KB.
  if (url === '/api/compile' && req.method === 'POST') {
    // Rate limiting (in-memory, per-IP)
    const clientIP =
      (req.headers['x-forwarded-for'] as string)?.split(',')[0]?.trim() ||
      req.socket.remoteAddress ||
      'unknown';
    const now = Date.now();
    if (!compileRateMap.has(clientIP)) compileRateMap.set(clientIP, []);
    const timestamps = compileRateMap.get(clientIP)!;
    // Prune old entries (older than 60s)
    while (timestamps.length > 0 && timestamps[0] < now - 60_000) timestamps.shift();
    if (timestamps.length >= 60) {
      res.writeHead(429, {
        'Content-Type': 'application/json; charset=utf-8',
        'Retry-After': '60',
      });
      res.end(JSON.stringify({ error: 'Rate limit exceeded. 60 requests per minute.' }));
      return;
    }
    timestamps.push(now);

    try {
      const body = await parseJsonBody(req);

      // Input size limit (100KB)
      if (typeof body.code === 'string' && body.code.length > 100_000) {
        res.writeHead(413, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Input too large. Maximum 100KB.' }));
        return;
      }

      if (!body.code || typeof body.code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code (string)' }));
        return;
      }
      if (!body.target || typeof body.target !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error:
              'Missing required field: target (string). Available: unity, unreal, godot, urdf, sdf, dtdl, webgpu, wasm, openxr, visionos, vrchat, ios, android, android-xr, nir, node-service, a2a-agent-card, state',
          })
        );
        return;
      }
      // Same code path as MCP tool `compile_holoscript` (handleCompilerTool dispatch).
      const result = await handleCompilerTool('compile_holoscript', {
        code: body.code,
        target: body.target,
        options: body.options || {},
      });
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(result));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/render — render HoloScript to preview
  if (url === '/api/render' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      if (!body.code || typeof body.code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code (string)' }));
        return;
      }
      const result = await renderPreview({
        code: body.code as string,
        format: (body.format as 'png' | 'gif' | 'mp4' | 'webp') || 'png',
        resolution: (body.resolution as number[]) || [800, 600],
        camera: body.camera as { position?: number[]; target?: number[] },
        duration: (body.duration as number) || 3000,
        quality: (body.quality as 'draft' | 'preview' | 'production') || 'preview',
        skipRemote: true,
      });
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(result));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/compile/webgpu-preview — compile HoloScript to sovereign WebGPU HTML
  // This is the deploy-path entry point for the sovereign render surface (task h7yz).
  // Returns a self-contained HTML page that boots WebGPURenderer with no Three.js dependency.
  if (url === '/api/compile/webgpu-preview' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      if (!body.code || typeof body.code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code (string)' }));
        return;
      }
      const result = await handleCompileToTarget({
        code: body.code as string,
        target: 'webgpu',
        options: body.options ?? {},
      });
      if (!result.success || !result.previewHtml) {
        res.writeHead(422, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: result.error ?? 'WebGPU compilation produced no output' }));
        return;
      }
      res.writeHead(200, {
        'Content-Type': 'text/html; charset=utf-8',
        'X-HoloScript-Renderer': 'sovereign-webgpu',
        'X-HoloScript-Compile-Ms': String(result.metadata.compilationTimeMs),
      });
      res.end(result.previewHtml);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /mcp — Stateless JSON-RPC HTTP transport (Railway CDN friendly)
  if (url === '/mcp' && req.method === 'POST') {
    let auth = await authenticateRequest(req);

    try {
      const rawBody = (await parseJsonBody(req)) as Record<string, unknown>;
      const unwrapped = await unwrapSignedHttpBody(rawBody, auth);
      if (unwrapped.signingError) {
        res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id: isRecord(rawBody.body) ? rawBody.body.id : rawBody.id,
            error: { code: -32001, message: 'signing-rejected', data: { reason: unwrapped.signingError } },
          })
        );
        return;
      }
      const body = unwrapped.body;
      const method = typeof body.method === 'string' ? body.method : '';
      const id = body.id;

      const params = (body.params as Record<string, unknown>) || {};
      const name = typeof params.name === 'string' ? params.name : '';
      const isFreeTool =
        method === 'tools/call' &&
        (PUBLIC_ANON_TOOLS.has(name) || name === 'parse_hs' || name === 'health');

      if (!auth.active) {
        if (isFreeTool) {
          auth = { active: true, scopes: ['tools:read'], agentId: 'anonymous' };
        } else {
          res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(JSON.stringify({ error: 'Unauthorized', message: 'Valid token required' }));
          return;
        }
      }

      if (method === 'initialize') {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id,
            result: {
              protocolVersion:
                typeof params?.protocolVersion === 'string' ? params.protocolVersion : '2025-11-25',
              capabilities: {
                tools: { listChanged: true },
                resources: {},
              },
              serverInfo: {
                name: SERVICE_NAME,
                version: SERVICE_VERSION,
              },
            },
          })
        );
        return;
      }

      if (!id && method.startsWith('notifications/')) {
        res.writeHead(202, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end('');
        return;
      }

      if (method === 'ping') {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ jsonrpc: '2.0', id, result: {} }));
        return;
      }

      if (method === 'tools/list') {
        const allTools = [...tools, ...PluginManager.getTools()];
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id: id,
            result: { tools: allTools },
          })
        );
        return;
      }

      if (method === 'resources/list') {
        const resources = listSkillResources();
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id,
            result: { resources },
          })
        );
        return;
      }

      if (method === 'resources/read') {
        const uri = typeof params.uri === 'string' ? params.uri : '';
        const result = readSkillResource(uri);
        if (!result) {
          res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(
            JSON.stringify({
              jsonrpc: '2.0',
              id,
              error: { code: -32002, message: `Resource not found: ${uri}` },
            })
          );
          return;
        }
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id,
            result: {
              contents: [
                {
                  uri,
                  mimeType: result.mimeType,
                  text: result.text,
                },
              ],
            },
          })
        );
        return;
      }

      if (method === 'tools/call') {
        const toolArgs = (params.arguments as Record<string, unknown>) || {};
        const tcpPeer = req.socket.remoteAddress?.replace(/^::ffff:/, '') || undefined;
        const { result, isError } = await securedToolExecution(name, toolArgs || {}, auth, {
          requestPath: '/mcp',
          requestMethod: 'POST',
          ip: clientIP,
          tcpPeerIp: tcpPeer,
          rawXForwardedFor: readXForwardedFor(req),
          bearerToken: readBearerToken(req),
          signingCtx: unwrapped.signingCtx,
        });

        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id: body.id,
            ...(isError
              ? {
                  error: {
                    code: -32000,
                    message: typeof result === 'string' ? result : JSON.stringify(result),
                  },
                }
              : {
                  result: {
                    content: [
                      {
                        type: 'text',
                        text: typeof result === 'string' ? result : JSON.stringify(result, null, 2),
                      },
                    ],
                  },
                }),
          })
        );
        return;
      }

      // Fallback for other methods
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          jsonrpc: '2.0',
          id: body.id,
          error: { code: -32601, message: 'Method not found or supported via stateless HTTP' },
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ jsonrpc: '2.0', error: { code: -32000, message } }));
    }
    return;
  }

  // POST /tools/call — REST proxy for orchestrator tool execution
  if (url === '/tools/call' && req.method === 'POST') {
    try {
      const rawBody = await parseJsonBody(req);
      const requestAuth = await authenticateRequest(req);
      if (!requestAuth.active) {
        res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            success: false,
            error: 'Unauthorized',
            message: 'Valid token required',
          })
        );
        return;
      }
      const unwrapped = await unwrapSignedHttpBody(rawBody, requestAuth);
      if (unwrapped.signingError) {
        res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ success: false, error: 'signing-rejected', reason: unwrapped.signingError }));
        return;
      }
      const body = unwrapped.body;
      const { tool, args } = body as { tool: string; args?: Record<string, unknown> };
      if (!tool || typeof tool !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: tool (string)' }));
        return;
      }

      const { result, isError } = await securedToolExecution(tool, args || {}, requestAuth, {
        requestPath: '/tools/call',
        requestMethod: 'POST',
        signingCtx: unwrapped.signingCtx,
      });

      res.writeHead(isError ? 500 : 200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ success: !isError, result }));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/share — create share links for X/social platforms
  if (url === '/api/share' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      if (!body.code || typeof body.code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code (string)' }));
        return;
      }
      const result = await createShareLink({
        code: body.code as string,
        title: (body.title as string) || 'HoloScript Scene',
        description: (body.description as string) || 'Interactive 3D scene built with HoloScript',
        platform: (body.platform as 'x' | 'generic' | 'codesandbox' | 'stackblitz') || 'x',
        skipRemote: true,
      });
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(result));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/scene — store a scene and get short URL
  if (url === '/api/scene' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      if (!body.code || typeof body.code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code (string)' }));
        return;
      }
      const scene = storeScene(
        body.code as string,
        (body.title as string) || undefined,
        (body.description as string) || undefined
      );
      const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
        ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
        : `http://localhost:${PORT}`;
      res.writeHead(201, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          id: scene.id,
          url: `${baseUrl}/scene/${scene.id}`,
          embed: `${baseUrl}/embed/${scene.id}`,
          api: `${baseUrl}/api/scene/${scene.id}`,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/deploy — deploy a composition with provenance metadata
  if (url === '/api/deploy' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      if (!body.code || typeof body.code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code (string)' }));
        return;
      }
      const scene = storeScene(body.code as string, {
        title: (body.title as string) || undefined,
        description: (body.description as string) || undefined,
        author: (body.author as string) || undefined,
        license: (body.license as string) || undefined,
        provenance: (body.provenance as SceneProvenance | undefined) || undefined,
      });
      const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
        ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
        : `http://localhost:${PORT}`;
      res.writeHead(201, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          id: scene.id,
          url: `${baseUrl}/scene/${scene.id}`,
          embed: `${baseUrl}/embed/${scene.id}`,
          api: `${baseUrl}/api/scene/${scene.id}`,
          provenance: scene.provenance
            ? {
                hash: scene.provenance.hash,
                publishMode: scene.provenance.publishMode,
                license: scene.license,
              }
            : undefined,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // GET /api/registry/:username/:name — fetch published composition by creator namespace
  if (url?.startsWith('/api/registry/') && req.method === 'GET') {
    const parts = url.replace('/api/registry/', '').split('/');
    if (parts.length === 2) {
      const [username, name] = parts.map(decodeURIComponent);
      const found = findSceneByAuthor(username, name);
      if (found) {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            id: found.id,
            code: found.code,
            title: found.title,
            author: found.author,
            license: found.license,
            provenance: found.provenance,
          })
        );
      } else {
        res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: `Composition @${username}/${name} not found` }));
      }
    } else {
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Expected /api/registry/:username/:name' }));
    }
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STUDIO PUBLISH ENDPOINTS — Wire extraction + publish UI to protocol
  // ═══════════════════════════════════════════════════════════════════════════

  // POST /api/publish — Full publish flow from Studio
  // Accepts scene payload, extracts traits, stores scene, registers protocol record,
  // stores provenance metadata, returns public URL.
  if (url === '/api/publish' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const code = body.code as string;
      if (!code || typeof code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code' }));
        return;
      }

      const metadata = (body.metadata as Record<string, unknown>) ?? {};
      const title = (metadata.name as string) || (body.title as string) || 'HoloScript Scene';
      const author = (body.author as string) || 'anonymous';
      const license = (body.license as string) || 'free';
      const price = (body.price as string) || '0';
      const visibility = (body.visibility as string) || 'public';
      const tags = (body.tags as string[]) || [];

      // 1. Generate content hash from source code
      const contentHash = createHash('sha256').update(code).digest('hex');

      // 2. Extract traits from the code (lightweight regex extraction)
      const traitMatches = code.match(/@\w+/g) ?? [];
      const traits = [...new Set(traitMatches)];

      // 3. Store the scene for public viewing
      const scene = storeScene(code, {
        title,
        author,
        license,
        provenance: {
          hash: contentHash,
          publishMode: 'original',
          imports: [],
        },
      });

      const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
        ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
        : `http://localhost:${PORT}`;
      const sceneUrl = `${baseUrl}/scene/${scene.id}`;
      const embedUrl = `${baseUrl}/embed/${scene.id}`;

      // 4. Register protocol record
      protocolRecords.set(contentHash, {
        contentHash,
        author,
        title,
        license,
        price,
        visibility,
        tags,
        traits,
        sceneId: scene.id,
        sceneUrl,
        embedUrl,
        timestamp: Date.now(),
      });

      // 5. Store provenance metadata
      protocolMetadata.set(contentHash, {
        provenance: {
          hash: contentHash,
          author,
          license,
          importHashes: [],
          timestamp: Date.now(),
        },
        sceneId: scene.id,
      });

      // 6. Preview revenue distribution if price > 0
      let revenue: Record<string, unknown> | null = null;
      if (price !== '0') {
        try {
          const { calculateRevenueDistribution } =
            (await import('@holoscript/core')) as unknown as {
              calculateRevenueDistribution: (
                price: bigint,
                creator: string,
                remixSources: string[]
              ) => RevenueDistributionResult;
            };
          const dist = calculateRevenueDistribution(BigInt(price), author, []);
          revenue = {
            totalPrice: dist.totalPrice.toString(),
            flows: dist.flows.map(
              (f: { recipient: string; amount: bigint; reason: string; bps: number }) => ({
                ...f,
                amount: f.amount.toString(),
              })
            ),
          };
        } catch {
          // Revenue preview is best-effort
        }
      }

      res.writeHead(201, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          url: sceneUrl,
          sceneId: scene.id,
          contentHash,
          embedUrl,
          traits,
          visibility,
          revenue,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/extract — Pre-publish trait extraction and revenue preview
  // Studio calls this before publishing to show the user what they're about to publish.
  if (url === '/api/extract' && req.method === 'POST') {
    try {
      const body = await parseJsonBody(req);
      const code = body.code as string;
      if (!code || typeof code !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: code' }));
        return;
      }

      const author = (body.author as string) || 'anonymous';
      const price = (body.price as string) || '0';

      // 1. Content hash
      const contentHash = createHash('sha256').update(code).digest('hex');

      // 2. Extract traits
      const traitMatches = code.match(/@\w+/g) ?? [];
      const traits = [...new Set(traitMatches)];

      // 3. Count objects
      const objectMatches = code.match(/^object\s+/gm) ?? [];
      const objectCount = objectMatches.length;

      // 4. Detect imports
      const importMatches = code.match(/^import\s+/gm) ?? [];
      const importCount = importMatches.length;

      // 5. Revenue preview if price > 0
      let revenue: Record<string, unknown> | null = null;
      if (price !== '0') {
        try {
          const { calculateRevenueDistribution } =
            (await import('@holoscript/core')) as unknown as {
              calculateRevenueDistribution: (
                price: bigint,
                creator: string,
                remixSources: string[]
              ) => RevenueDistributionResult;
            };
          const dist = calculateRevenueDistribution(BigInt(price), author, []);
          revenue = {
            totalPrice: dist.totalPrice.toString(),
            flows: dist.flows.map(
              (f: { recipient: string; amount: bigint; reason: string; bps: number }) => ({
                ...f,
                amount: f.amount.toString(),
              })
            ),
          };
        } catch {
          // Revenue preview best-effort
        }
      }

      // 6. Check if already published
      const existing = protocolRecords.get(contentHash);

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          contentHash,
          traits,
          objectCount,
          importCount,
          codeLength: code.length,
          alreadyPublished: !!existing,
          existingUrl: existing ? (existing.sceneUrl as string) : null,
          revenue,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PROTOCOL ENDPOINTS (HoloScript-as-Protocol — Publishing & Collecting)
  // ═══════════════════════════════════════════════════════════════════════════

  // POST /api/protocol — Register a protocol record
  if (url === '/api/protocol' && req.method === 'POST') {
    const auth = await authenticateRequest(req);
    if (!auth.active) {
      auditLog.logAuthEvent({
        event: 'auth_failure',
        ip: clientIP,
        reason: '/api/protocol publish - invalid credentials',
      });
      res.writeHead(401, {
        'Content-Type': 'application/json; charset=utf-8',
        'WWW-Authenticate': 'Bearer realm="holoscript-mcp", error="invalid_token"',
      });
      res.end(
        JSON.stringify({
          error: 'Unauthorized',
          message:
            'Publishing to the HoloScript Protocol requires authentication. Register a client via POST /oauth/register and use the issued bearer token.',
          token_endpoint: '/oauth/token',
          registration_endpoint: '/oauth/register',
        })
      );
      return;
    }
    try {
      const body = await parseJsonBody(req);
      const contentHash = body.contentHash as string;
      if (!contentHash) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'Missing required field: contentHash' }));
        return;
      }
      // Store record in protocol registry
      protocolRecords.set(contentHash, {
        ...(body as Record<string, unknown>),
        contentHash,
        timestamp: body.timestamp || Date.now(),
      });
      // If source code was provided, store as scene too
      const sceneResult: Record<string, unknown> = {};
      if (body.source && typeof body.source === 'string') {
        const scene = storeScene(body.source as string, {
          title: (body.title as string) || undefined,
          author: (body.author as string) || undefined,
          license: (body.license as string) || undefined,
        });
        const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
          ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
          : `http://localhost:${PORT}`;
        sceneResult.sceneId = scene.id;
        sceneResult.sceneUrl = `${baseUrl}/scene/${scene.id}`;
        sceneResult.embedUrl = `${baseUrl}/embed/${scene.id}`;
      }
      res.writeHead(201, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(sceneResult));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // POST /api/protocol/metadata — Store provenance metadata
  if (url === '/api/protocol/metadata' && req.method === 'POST') {
    const auth = await authenticateRequest(req);
    if (!auth.active) {
      res.writeHead(401, {
        'Content-Type': 'application/json; charset=utf-8',
        'WWW-Authenticate': 'Bearer realm="holoscript-mcp", error="invalid_token"',
      });
      res.end(
        JSON.stringify({
          error: 'Unauthorized',
          message: 'Storing protocol metadata requires authentication. See POST /oauth/register.',
          registration_endpoint: '/oauth/register',
        })
      );
      return;
    }
    try {
      const body = await parseJsonBody(req);
      const provenance = body.provenance as Record<string, unknown> | undefined;
      const hash = (provenance?.hash as string) || `meta-${Date.now()}`;
      protocolMetadata.set(hash, body);
      const baseUrl = process.env.RAILWAY_PUBLIC_DOMAIN
        ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`
        : `http://localhost:${PORT}`;
      res.writeHead(201, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ metadataURI: `${baseUrl}/metadata/${hash}` }));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // GET /api/protocol/revenue/:hash — Preview revenue distribution
  if (url?.startsWith('/api/protocol/revenue/') && req.method === 'GET') {
    const hash = url.replace('/api/protocol/revenue/', '');
    if (!hash) {
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Missing content hash' }));
      return;
    }
    try {
      const record = protocolRecords.get(hash);
      const price = BigInt((record?.price as string) || '0');
      const creator = (record?.author as string) || 'unknown';
      // Dynamic import to avoid loading core at startup if unused
      const { calculateRevenueDistribution, formatRevenueDistribution } =
        (await import('@holoscript/core')) as unknown as {
          calculateRevenueDistribution: (
            price: bigint,
            creator: string,
            remixSources: string[]
          ) => RevenueDistributionResult;
          formatRevenueDistribution: (dist: RevenueDistributionResult) => string[];
        };
      const dist = calculateRevenueDistribution(price, creator, []);
      const lines = formatRevenueDistribution(dist);
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          totalPrice: dist.totalPrice.toString(),
          flows: dist.flows.map(
            (f: {
              recipient: string;
              amount: bigint;
              reason: string;
              bps: number;
              depth?: number;
            }) => ({
              ...f,
              amount: f.amount.toString(),
            })
          ),
          formatted: lines,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // GET /api/protocol/author/:address — Get all publications by author
  if (url?.startsWith('/api/protocol/author/') && req.method === 'GET') {
    const author = decodeURIComponent(url.replace('/api/protocol/author/', ''));
    const records = Array.from(protocolRecords.values()).filter(
      (r: Record<string, unknown>) => r.author === author
    );
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(records));
    return;
  }

  // GET /api/protocol/:hash — Get protocol record by content hash
  if (url?.startsWith('/api/protocol/') && req.method === 'GET') {
    const hash = url.replace('/api/protocol/', '');
    if (!hash || hash.includes('/')) {
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Invalid content hash' }));
      return;
    }
    const record = protocolRecords.get(hash);
    if (!record) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Protocol record not found' }));
      return;
    }
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(record));
    return;
  }

  // GET /api/public/tool — Discovery for the anonymous tier
  if (url === '/api/public/tool' && req.method === 'GET') {
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(
      JSON.stringify({
        description: 'Anonymous tier — read-only HoloScript tools, no API key required.',
        tools: Array.from(PUBLIC_ANON_TOOLS),
        rate_limit_per_minute: PUBLIC_ANON_RATE_LIMIT,
        usage: {
          method: 'POST',
          content_type: 'application/json',
          body_schema: { tool: 'string', arguments: 'object' },
          example: {
            tool: 'parse_holo',
            arguments: { code: 'composition "Hello" { object "Box" {} }' },
          },
        },
        upgrade: {
          endpoint: '/oauth/register',
          why: 'Higher rate limits and access to all MCP tools (compile, generate, codebase intelligence, etc.).',
        },
      })
    );
    return;
  }

  // POST /api/public/tool — Anonymous read-only tool dispatch
  if (url === '/api/public/tool' && req.method === 'POST') {
    const rl = getRateLimit(`anon:${clientIP}`, PUBLIC_ANON_RATE_LIMIT);
    setRateLimitHeaders(res, rl);
    if (rl.remaining === 0) {
      res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'rate_limit_exceeded',
          message: `Anonymous tier limited to ${PUBLIC_ANON_RATE_LIMIT} requests/minute per IP. Register a client at POST /oauth/register for higher limits.`,
          retry_after_seconds: Math.max(1, Math.ceil((rl.resetAt - Date.now()) / 1000)),
        })
      );
      return;
    }

    try {
      const body = await parseJsonBody(req);
      const tool = body.tool as string | undefined;
      const args = (body.arguments as Record<string, unknown> | undefined) || {};

      if (!tool || typeof tool !== 'string') {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error: 'invalid_request',
            message: 'Missing required field: tool (string)',
            example: { tool: 'parse_holo', arguments: { code: 'composition "Hello" {}' } },
          })
        );
        return;
      }

      if (!PUBLIC_ANON_TOOLS.has(tool)) {
        res.writeHead(403, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error: 'tool_not_public',
            message: `Tool '${tool}' is not available on the anonymous tier.`,
            available_anonymous_tools: Array.from(PUBLIC_ANON_TOOLS),
            upgrade: {
              endpoint: '/oauth/register',
              why: 'Register a client to access all MCP tools.',
            },
          })
        );
        return;
      }

      const result = await _handleSingleToolLogic(tool, args);
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify(result));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'tool_execution_failed', message }));
    }
    return;
  }

  // GET /api/public/feed — WS-2 sovereign-web consumer loop MVP: public feed
  // of shared worlds. Read-only, in-memory list operation -- deliberately NOT
  // layered with rate limiting/quota/spend-cap machinery like the generation
  // endpoints above; that machinery bounds LLM-generation cost, which does not
  // apply here. Only scenes explicitly opted in via listPublic:true (set at
  // exactly 2 call sites: POST /api/public/generate and createShareLink()) are
  // returned -- every other storeScene() call site in this file is untouched
  // and stays feed-invisible by default.
  if (url?.startsWith('/api/public/feed') && req.method === 'GET') {
    // Base is a parser anchor only (WHATWG URL requires an absolute base to
    // parse a path+query string) -- no network request is made. Matches the
    // existing `new URL(url, 'http://localhost')` pattern used elsewhere in
    // this file for incoming-request query parsing (e.g. line ~2691).
    const parsedUrl = new URL(url, 'http://localhost');
    const limitParam = parseInt(parsedUrl.searchParams.get('limit') || '20', 10);
    const limit = Number.isFinite(limitParam) ? Math.min(50, Math.max(1, limitParam)) : 20;

    const scenes = listRecentPublicScenes(limit).map((scene) => ({
      ...scene,
      previewUrl: `/scene/${scene.id}`,
    }));

    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify({ scenes }));
    return;
  }

  // POST /api/public/generate — WS-1 anonymous consumer generation tier.
  // Deliberately NOT part of PUBLIC_ANON_TOOLS/_handleSingleToolLogic above --
  // this routes through the REAL ForkSandboxGate (handleTool -> runForkSandboxGate
  // with source:'consumer'), which only allows a non-denying policy for the two
  // allowlisted consumer-generation tools. Every other sensitive tool is
  // completely unreachable from this endpoint (CONSUMER_GENERATION_TOOLS is a
  // fixed allowlist of exactly 2 tool names).
  // Spend layering: the per-IP daily quota below bounds cost PER CALLER, but
  // has no ceiling on aggregate cost across many distinct anonymous IPs each
  // hitting their own quota. checkConsumerGlobalSpendCap()
  // (security/consumer-spend-guard.ts) adds a SHARED rolling-24h call-count
  // cap on top of the per-IP quota to bound total exposure to paid frontier
  // LLM fallthrough (see that module's header for the full gap writeup).
  if (url === '/api/public/generate' && req.method === 'POST') {
    // Per-minute rate limit (bounds request RATE).
    const rl = getRateLimit(`consumer-gen:${clientIP}`, CONSUMER_GEN_RATE_LIMIT);
    setRateLimitHeaders(res, rl);
    if (rl.remaining === 0) {
      res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'rate_limit_exceeded',
          message: `Consumer generation tier limited to ${CONSUMER_GEN_RATE_LIMIT} requests/minute per IP.`,
          retry_after_seconds: Math.max(1, Math.ceil((rl.resetAt - Date.now()) / 1000)),
        })
      );
      return;
    }

    // Mandatory daily quota (bounds total distinct-IP fan-out COST against an
    // LLM -- founder decision 2026-07-02, see the resolvePolicy() comment above).
    const quota = checkConsumerGenDailyQuota(clientIP);
    if (!quota.allowed) {
      res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'daily_quota_exceeded',
          message: `Consumer generation tier limited to ${CONSUMER_GEN_DAILY_QUOTA} generations/day per IP.`,
          resets_at: new Date(quota.resetAt).toISOString(),
        })
      );
      return;
    }

    // Global (cross-IP) daily call-count cap -- bounds AGGREGATE spend across
    // all anonymous callers, on top of the per-IP quota above (see
    // security/consumer-spend-guard.ts header for the gap this closes).
    // Fail-closed: any thrown error is treated as denied, never allowed.
    try {
      const globalCap = await checkConsumerGlobalSpendCap();
      if (!globalCap.allowed) {
        res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error: 'global_capacity_exceeded',
            message:
              'The shared daily generation budget for the anonymous consumer tier is exhausted across all users.',
            resets_at: new Date(globalCap.resetAt).toISOString(),
          })
        );
        return;
      }
    } catch {
      res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          error: 'global_capacity_exceeded',
          message: 'The shared daily generation budget check failed; failing closed.',
        })
      );
      return;
    }

    // Abuse/evasion heuristics (subnet fan-out, bearer-token rotation, XFF spoofing) --
    // the same detector every other MCP surface uses (http-server.ts:629 precedent).
    try {
      const bypass = await checkRateLimitBypass({
        toolName: 'generate_scene_or_world',
        directIp: clientIP,
        rawXForwardedFor: readXForwardedFor(req),
      });
      if (!bypass.allowed) {
        res.writeHead(429, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ error: 'bypass_denied', code: bypass.reason || 'bypass_denied' }));
        return;
      }
    } catch {
      // Fail-open on the detector's OWN internal error only (never on a real denial,
      // which is handled above) -- an availability bug in the heuristic must not
      // become an outage for legitimate anonymous traffic.
    }

    try {
      const body = await parseJsonBody(req);
      const tool = body.tool as string | undefined;
      const args = (body.arguments as Record<string, unknown> | undefined) || {};

      if (!tool || typeof tool !== 'string' || !CONSUMER_GENERATION_TOOLS.has(tool)) {
        res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            error: 'invalid_request',
            message: `tool must be one of: ${Array.from(CONSUMER_GENERATION_TOOLS).join(', ')}`,
          })
        );
        return;
      }

      // Input content-policy screen (P.013 pattern, same as hololand-mcp-tools.ts
      // NPC dialogue screening) BEFORE any model spend.
      // generate_scene's schema field is `description` (handlers.ts:1171);
      // generate_world_from_prompt's is `prompt` (hololand-mcp-tools.ts:332,
      // resolveWorldPrompt reads args.prompt only) -- confirmed live 2026-07-02
      // (a request with only `description` set hit generate_world_from_prompt's
      // "prompt or at least one multimodal input is required" early return).
      // Check both so either allowlisted tool's real user-text field is screened.
      const promptText =
        typeof args.description === 'string'
          ? args.description
          : typeof args.prompt === 'string'
            ? args.prompt
            : '';
      if (promptText) {
        const inputDecision = evaluateContentPolicySync(
          { text: promptText, surface: 'consumer-generation', direction: 'input' },
          consumerGenerationPolicyConfig
        );
        if (!inputDecision.allowed) {
          res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
          res.end(
            JSON.stringify({
              success: false,
              error: 'Input blocked by content policy.',
              action: inputDecision.action,
              category: inputDecision.category,
            })
          );
          return;
        }
      }

      // The real gate: handleTool() -> runForkSandboxGate({source:'consumer'}).
      // The consumer source resolves to DEFAULT_CONSUMER_GENERATION_POLICY only
      // for the two allowlisted tools; all other tool names stay sensitive.
      const gated = await handleTool(tool, args, undefined, 'consumer');
      // handleTool() returns the RAW handler object directly (no MCP
      // content[]-envelope wrapping -- that only happens at the JSON-RPC/
      // stdio protocol layer, which this endpoint deliberately bypasses).
      // Confirmed live 2026-07-02 (local http-server.ts boot, mock provider):
      // generate_scene returns {code, stats, source, provider, ...} (no
      // `success` field), generate_world_from_prompt returns {success:true,
      // holoCode, source, format, ...} (hololand-mcp-tools.ts:2160-2175) --
      // two different top-level field names for the code. A gate/content-
      // policy denial always has success:false regardless of tool (set at
      // the top of handleTool(), before either tool's handler ever runs).
      const gatedRecord = gated as {
        success?: boolean;
        error?: string;
        code?: string;
        holoCode?: string;
      };
      if (gatedRecord?.success === false) {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify(gatedRecord));
        return;
      }

      const generatedCode =
        typeof gatedRecord?.code === 'string'
          ? gatedRecord.code
          : typeof gatedRecord?.holoCode === 'string'
            ? gatedRecord.holoCode
            : '';

      if (!generatedCode) {
        res.writeHead(502, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(JSON.stringify({ success: false, error: 'generation_produced_no_code' }));
        return;
      }

      // Output content-policy screen BEFORE returning generated content to the client.
      const outputDecision = evaluateContentPolicySync(
        { text: generatedCode, surface: 'consumer-generation', direction: 'output' },
        consumerGenerationPolicyConfig
      );
      if (!outputDecision.allowed) {
        res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            success: false,
            error: 'Output blocked by content policy.',
            action: outputDecision.action,
            category: outputDecision.category,
          })
        );
        return;
      }

      // Structural-validity backstop: reject garbage/empty output using the SAME
      // parser every other HoloScript surface trusts (parseHolo, strict mode) --
      // reused directly rather than importing studio's cross-package validator.
      const parsedHolo = parseHolo(generatedCode, { tolerant: false, strict: true });
      if (!parsedHolo.success || !parsedHolo.ast) {
        res.writeHead(502, { 'Content-Type': 'application/json; charset=utf-8' });
        res.end(
          JSON.stringify({
            success: false,
            error: 'generated_output_failed_structural_validation',
            details: parsedHolo.errors,
          })
        );
        return;
      }

      // Route through the capped, LRU-evicted preview store -- NEVER world
      // persistence. generate_world_from_prompt's own handler already never
      // calls worldRegistry.set (only handleCreateWorld does); this is a second,
      // independent guarantee at the HTTP boundary.
      const stored = storeScene(generatedCode, {
        title: 'Consumer-generated scene',
        description: promptText || 'Generated via the anonymous consumer tier',
        listPublic: true,
      });

      // Budget is spent ONLY here, on a genuinely successful, validated
      // generation -- never on a gate denial, content-policy block, or
      // structural-validation failure (see consumer-spend-guard.ts).
      const { receiptId } = await recordConsumerGeneration();

      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          success: true,
          sceneId: stored.id,
          previewUrl: `/scene/${stored.id}`,
          code: generatedCode,
          receiptId,
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(500, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'tool_execution_failed', message }));
    }
    return;
  }

  // POST /api/collect/:hash — Collect a published composition
  if (url?.startsWith('/api/collect/') && req.method === 'POST') {
    const auth = await authenticateRequest(req);
    if (!auth.active) {
      auditLog.logAuthEvent({
        event: 'auth_failure',
        ip: clientIP,
        reason: '/api/collect collect - invalid credentials',
      });
      res.writeHead(401, {
        'Content-Type': 'application/json; charset=utf-8',
        'WWW-Authenticate': 'Bearer realm="holoscript-mcp", error="invalid_token"',
      });
      res.end(
        JSON.stringify({
          error: 'Unauthorized',
          message:
            'Collecting an edition requires authentication. Register a client via POST /oauth/register and use the issued bearer token.',
          token_endpoint: '/oauth/token',
          registration_endpoint: '/oauth/register',
        })
      );
      return;
    }
    const hash = url.replace('/api/collect/', '');
    const record = protocolRecords.get(hash);
    if (!record) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Composition not found' }));
      return;
    }
    try {
      const body = await parseJsonBody(req);
      const quantity = (body.quantity as number) || 1;
      // Increment edition count
      const currentCount = (record.editionCount as number) || 0;
      record.editionCount = currentCount + quantity;
      const editions = Array.from({ length: quantity }, (_, i) => currentCount + i + 1);
      res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(
        JSON.stringify({
          editions,
          txHash: '',
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      res.writeHead(400, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: message }));
    }
    return;
  }

  // GET /metadata/:hash — Retrieve stored metadata
  if (url?.startsWith('/metadata/') && req.method === 'GET') {
    const hash = url.replace('/metadata/', '');
    const meta = protocolMetadata.get(hash);
    if (!meta) {
      res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Metadata not found' }));
      return;
    }
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(meta));
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMIN OPERATIONS AUDIT (P.009.01) — founder HTTP actions, ring buffer + optional KV flush
  // ═══════════════════════════════════════════════════════════════════════════

  if (url === '/admin/audit' && req.method === 'GET') {
    const auth = await authenticateRequest(req);
    if (!auth.active) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Authentication required for admin audit access' }));
      return;
    }
    if (!auth.scopes?.includes('admin:*') && !auth.scopes?.includes('tools:admin')) {
      res.writeHead(403, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Insufficient scope. Requires admin:* or tools:admin' }));
      return;
    }

    const queryString = req.url?.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const limitRaw = params.get('limit');
    const limit = limitRaw ? parseInt(limitRaw, 10) : 50;

    const result = queryAdminOperationsAudit(limit);
    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify({ success: true, ...result }, null, 2));
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AUDIT LOG ENDPOINT (EU AI Act Art. 13: Transparency)
  // ═══════════════════════════════════════════════════════════════════════════

  if (url === '/api/audit' && req.method === 'GET') {
    // Audit logs require authentication
    const auth = await authenticateRequest(req);
    if (!auth.active) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Authentication required for audit access' }));
      return;
    }

    // Only admin scope can view audit logs
    if (!auth.scopes?.includes('admin:*') && !auth.scopes?.includes('tools:admin')) {
      res.writeHead(403, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Insufficient scope. Requires admin:* or tools:admin' }));
      return;
    }

    const queryString = req.url?.split('?')[1] || '';
    const params = new URLSearchParams(queryString);

    const result = auditLog.query({
      event: (params.get('event') as AuditEventType) || undefined,
      clientId: params.get('clientId') || undefined,
      toolName: params.get('toolName') || undefined,
      status: (params.get('status') as AuditResultStatus) || undefined,
      riskLevel: (params.get('riskLevel') as 'low' | 'medium' | 'high' | 'critical') || undefined,
      since: params.get('since') || undefined,
      until: params.get('until') || undefined,
      limit: params.get('limit') ? parseInt(params.get('limit')!, 10) : 100,
      offset: params.get('offset') ? parseInt(params.get('offset')!, 10) : 0,
      humanReviewOnly: params.get('humanReviewOnly') === 'true',
    });

    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(result, null, 2));
    return;
  }

  // GET /api/audit/compliance — EU AI Act compliance summary
  if (url === '/api/audit/compliance' && req.method === 'GET') {
    const auth = await authenticateRequest(req);
    if (
      !auth.active ||
      (!auth.scopes?.includes('admin:*') && !auth.scopes?.includes('tools:admin'))
    ) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Authentication required with admin scope' }));
      return;
    }

    res.writeHead(200, { 'Content-Type': 'application/json; charset=utf-8' });
    res.end(JSON.stringify(auditLog.getComplianceStats(), null, 2));
    return;
  }

  // GET /api/audit/export — Export audit logs (EU AI Act Art. 13)
  if (url === '/api/audit/export' && req.method === 'GET') {
    const auth = await authenticateRequest(req);
    if (
      !auth.active ||
      (!auth.scopes?.includes('admin:*') && !auth.scopes?.includes('tools:admin'))
    ) {
      res.writeHead(401, { 'Content-Type': 'application/json; charset=utf-8' });
      res.end(JSON.stringify({ error: 'Authentication required with admin scope' }));
      return;
    }

    const queryString = req.url?.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const format = (params.get('format') || 'json') as 'json' | 'jsonl';

    const contentType = format === 'jsonl' ? 'application/x-ndjson' : 'application/json';
    res.writeHead(200, {
      'Content-Type': contentType,
      'Content-Disposition': `attachment; filename="holoscript-audit-${new Date().toISOString().split('T')[0]}.${format}"`,
    });
    res.end(auditLog.export(format));
    return;
  }

  // Root URL — redirect to discovery endpoint
  if (url === '/' && req.method === 'GET') {
    res.writeHead(302, { Location: '/.well-known/mcp' });
    res.end();
    return;
  }

  // 404 for everything else
  res.writeHead(404, { 'Content-Type': 'application/json; charset=utf-8' });
  res.end(JSON.stringify({ error: 'Not Found' }));
});

// ── Start Server ─────────────────────────────────────────────────────────────

// Initialize WebRTC Signaling Server for Neural Streaming out-of-band payloads
new WebRTCSignalingServer(httpServer, '/webrtc-signaling');

// Load team, social, and agent state
(async () => {
  await initStores();

  // Wire holomesh_search's data providers (task_1784578782174_oo87 — the
  // module-level agentProvider/entryProvider in search.ts stayed null forever
  // because registerSearchProviders() was never called, so holomesh_search
  // silently returned 0 results for every query). agentProvider reads the
  // same in-memory registered-agent store identity/auth code uses; entryProvider
  // delegates to the orchestrator client's existing cross-workspace knowledge
  // query (the same call the reputation/contribution code paths already use).
  registerSearchProviders(
    () =>
      Array.from(agentKeyStore.values()).map((agent) => ({
        id: agent.id,
        name: agent.name,
        traits: agent.traits || [],
        reputation: agent.reputation || 0,
        profile: {
          bio: typeof agent.profile?.bio === 'string' ? agent.profile.bio : undefined,
          statusText:
            typeof agent.profile?.statusText === 'string' ? agent.profile.statusText : undefined,
        },
      })),
    (query: string, opts?: { type?: string; limit?: number }) =>
      getHoloMeshOrchestratorClient().queryKnowledge(query, opts)
  );

  // Wire holomesh_team_form's team_id roster source (task_1784579983269_ragg —
  // same never-called DI pattern flagged, but not fixed, alongside the search
  // fix above: setTeamFormationRosterSource() was exported from
  // team-formation-tools.ts but never invoked, so `holomesh_team_form` could
  // only ever succeed with an inline `roster` — passing `team_id` always
  // returned the explicit "No roster source configured" error). Real data,
  // not a stub: team membership comes from the same teamStore/reloadTeam
  // pair route handlers use for request-boundary freshness; each member's
  // capabilities/reputation are cross-referenced from the same agentKeyStore
  // identity map the search fix above already wires as agentProvider;
  // liveness comes from the same teamPresenceStore heartbeat map the
  // board/presence routes read.
  setTeamFormationRosterSource({
    fetchRoster: async (teamId: string) => {
      await reloadTeam(teamId);
      const team = teamStore.get(teamId);
      if (!team) return [];

      const agentsById = new Map(Array.from(agentKeyStore.values()).map((a) => [a.id, a]));
      const presence = teamPresenceStore.get(teamId);

      return team.members.map((member) => {
        const registered = agentsById.get(member.agentId);
        // RosterAgent scores are 0..1; reputation is an unbounded tier score
        // (100+ = 'authority'), so normalize onto the same scale the
        // team-formation scorer expects instead of passing the raw value.
        const score = registered ? Math.max(0, Math.min(1, registered.reputation / 100)) : undefined;
        const presenceEntry = presence?.get(member.agentId);
        return {
          agentId: member.agentId,
          agentName: member.agentName,
          capabilities: registered?.traits ?? [],
          specializationScore: score,
          performanceScore: score,
          active: presenceEntry ? presenceEntry.status !== 'offline' : true,
        };
      });
    },
  });

  // Rehydrate founder seat attestations from durable storage so a single
  // (Trezor) attestation survives restart instead of being wiped every deploy.
  // Additive + fail-safe: on load failure the registry starts empty (prior
  // behavior). Must run before listen() so /me/attestation reads the durable set.
  try {
    initDurableAttestationRegistry();
  } catch (e) {
    console.warn('[HoloMesh] durable attestation init failed (continuing empty):', e);
  }

  // Drain ci-public lane on Railway's spare CPU (zero marginal cost co-location).
  startCiPublicWorker().catch(() => {
    /* non-fatal */
  });

  // Rehydrate the daimōn emergence corpus (D.053) from durable storage so the
  // in-memory soul-observation / daemon Maps survive restart. Additive; never
  // blocks startup (best-effort, swallows errors).
  try {
    const stats = hydrateEmergenceFromCorpus();
    if (stats.records > 0) {
      console.info(
        `[emergence] hydrated corpus: ${stats.records} record(s), ${stats.souls} soul(s), ${stats.emerged} emerged`
      );
    }
  } catch (e) {
    console.warn('[emergence] corpus hydration failed (continuing):', e);
  }

  httpServer.listen(PORT, '0.0.0.0', () => {
    const migrationMode = process.env.OAUTH_MIGRATION_MODE || 'permissive';
    ensureMcpOtelTracer();
    const otlp = process.env.OTEL_EXPORTER_OTLP_ENDPOINT?.trim();
    console.info(`\u{1F680} ${SERVICE_NAME} v${SERVICE_VERSION}`);
    console.info(
      `   Transport: Streamable HTTP (default)${ALLOW_SSE_TRANSPORT ? ' + SSE session mode enabled' : ' (SSE session mode disabled on Railway)'}`
    );
    console.info(`   Port: ${PORT}`);
    console.info(`   Auth: OAuth 2.1 (migration: ${migrationMode})`);
    console.info(
      `   Sizing: ${SERVER_SIZING.profile} (${SERVER_SIZING.recommendedConsumer}, body=${SERVER_SIZING.requestBodyMaxBytes}B, pgPool=${SERVER_SIZING.postgresPoolMax}, tools=${SERVER_SIZING.maxConcurrentToolCalls}, timeout=${SERVER_SIZING.toolTimeoutMs}ms, oauth=${RATE_LIMIT}/min, anon=${PUBLIC_ANON_RATE_LIMIT}/min, gen=${CONSUMER_GEN_RATE_LIMIT}/min/${CONSUMER_GEN_DAILY_QUOTA}/day)`
    );
    console.info(
      `   Token TTL: access=${oauth2.getStore().ttl.accessTokenTTL}s, refresh=${oauth2.getStore().ttl.refreshTokenTTL}s`
    );
    console.info(`   Scopes: ${Object.keys(OAUTH2_SCOPES).join(', ')}`);
    console.info(
      `   Legacy API Key: ${HOLOSCRIPT_API_KEY ? 'configured' : 'NONE (open dev mode)'}`
    );
    console.info(`   Security: Triple-gate (prompt \u2192 scope \u2192 policy)`);
    console.info(`   Token Store: ${oauth2.getStore().backend.constructor.name}`);
    console.info(`   Audit: EU AI Act compliant (Articles 12-14)`);
    if (otlp) {
      console.info(`   OpenTelemetry: MCP tool spans → OTLP (${otlp})`);
    }
    console.info(`   Tools: ${tools.length} core + ${PluginManager.getTools().length} plugins`);
    console.info(
      `   Moltbook: ${process.env.MOLTBOOK_API_KEY ? 'heartbeat active' : 'disabled (no MOLTBOOK_API_KEY)'}`
    );
    console.info(`   Endpoints:`);
    console.info(`     GET  /health                       - Health check (public)`);
    console.info(`     GET  /.well-known/mcp              - MCP discovery (public)`);
    console.info(`     GET  /.well-known/openid-configuration - OAuth 2.1 discovery (public)`);
    console.info(`     GET  /.well-known/agent-card.json  - A2A Agent Card (public)`);
    console.info(`     POST /oauth/register               - Client registration`);
    console.info(`     GET  /oauth/authorize               - Authorization request (PKCE)`);
    console.info(`     POST /oauth/authorize              - Authorization code (PKCE)`);
    console.info(`     POST /oauth/token                  - Token exchange`);
    console.info(`     POST /oauth/revoke                 - Token revocation`);
    console.info(`     POST /oauth/introspect             - Token introspection (RFC 7662)`);
    console.info(`     POST /oauth/github/device          - GitHub device flow (RFC 8628)`);
    console.info(`     POST /mcp                          - MCP Streamable HTTP (authenticated)`);
    if (ALLOW_SSE_TRANSPORT) {
      console.info(
        `     GET  /mcp                          - MCP SSE session bootstrap (authenticated)`
      );
      console.info(
        `     POST /mcp/messages                 - MCP SSE session messages (authenticated)`
      );
    }
    console.info(`     GET  /a2a                          - A2A protocol info (public)`);
    console.info(`     POST /a2a                          - A2A JSON-RPC 2.0 transport`);
    console.info(`     POST /a2a/tasks                    - A2A send task (REST fallback)`);
    console.info(`     GET  /a2a/tasks                    - A2A list tasks`);
    console.info(`     GET  /a2a/tasks/:id                - A2A get task`);
    console.info(`     DELETE /a2a/tasks/:id              - A2A cancel task`);
    console.info(`     GET  /api/health                   - API health + capabilities (public)`);
    console.info(
      `     POST /api/moltbook/crosspost       - HoloMesh handoff / WPG knowledge -> Moltbook (auth)`
    );
    console.info(
      `     POST /api/compile                  - Compile HoloScript to any target (returns raw code)`
    );
    console.info(`     POST /api/render                   - Render HoloScript preview`);
    console.info(`     POST /api/share                    - Create share links`);
    console.info(`     POST /api/publish                  - Studio full publish flow`);
    console.info(`     POST /api/extract                  - Pre-publish trait extraction`);
    console.info(`     POST /api/scene                    - Store scene, get short URL`);
    console.info(`     GET  /scene/:id                    - View stored scene (public)`);
    console.info(`     GET  /embed/:id                    - Embed stored scene (public)`);
    console.info(
      `     WS   /webrtc-signaling             - WebRTC Signaling Bridge (Neural Streaming)`
    );
    console.info(`     GET  /admin/audit                  - Admin operations audit trail (admin)`);
    console.info(`     GET  /api/audit                    - Query audit log (admin)`);
    console.info(`     GET  /api/audit/compliance         - EU AI Act compliance report (admin)`);
    console.info(`     GET  /api/audit/export             - Export audit log (admin)`);
    maybeStartRailwayAutoscaleLoop({ port: PORT });
    maybeStartPredictiveCloudflareLbLoop();
    maybeStartKeepAliveLoop({ port: PORT });
  });
})();

// ─── Thermodynamic consolidation timer ─────────────────────────────────────
// Runs the biological sleep cycle every 6 hours via HoloMeshConsolidationBridge.
// The bridge wraps ConsolidationEngine from @holoscript/framework and adds:
//   - explicit trigger paths from HoloMesh knowledge + session reports
//   - timer / entropy / conflict / manual triggers
//   - reviewed quarantine output (safe Dreaming pattern)
//   - run logs with rollback / discard behavior
const CONSOLIDATION_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

function runConsolidationCycle(): void {
  try {
    const bridge = getConsolidationBridge();
    const results = bridge.triggerTimer();
    if (results.length > 0) {
      const totalPromoted = results.reduce((s, r) => s + r.promoted, 0);
      const totalEvicted = results.reduce((s, r) => s + r.evicted, 0);
      const totalMerged = results.reduce((s, r) => s + r.merged, 0);
      const totalDropped = results.reduce((s, r) => s + r.dropped, 0);
      const totalQuarantined = results.reduce((s, r) => s + r.quarantined, 0);
      const totalRejected = results.reduce((s, r) => s + r.rejected, 0);
      console.info(
        `[consolidation] Sleep cycle: ${results.length} domains consolidated — promoted:${totalPromoted} merged:${totalMerged} evicted:${totalEvicted} dropped:${totalDropped} quarantined:${totalQuarantined} rejected:${totalRejected}`
      );
    }
  } catch (err) {
    console.warn('[consolidation] Sleep cycle failed:', err instanceof Error ? err.message : err);
  }
}

// Run first consolidation 30s after startup, then every 6 hours
setTimeout(runConsolidationCycle, 30_000);
const consolidationTimer = setInterval(runConsolidationCycle, CONSOLIDATION_INTERVAL_MS);
consolidationTimer.unref(); // Don't prevent shutdown

// Graceful shutdown
function shutdown(signal: string) {
  console.info(`${signal} received, closing server...`);
  httpServer.close(() => {
    console.info('Server closed');
    process.exit(0);
  });
  // Force exit after 10s if connections don't drain
  setTimeout(() => {
    console.warn('Forcing exit after 10s timeout');
    process.exit(1);
  }, 10_000).unref();
}
process.on('SIGTERM', () => shutdown('SIGTERM'));
process.on('SIGINT', () => shutdown('SIGINT'));
